import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Navigation from "../components/Navigation";
import { usePageMeta, PAGE_META } from "../utils/usePageMeta";
import "./DocumentationPage.css";
import WorkerContinuityGuide from "./WorkerContinuityGuide";
import AgentClientsGuide from "./AgentClientsGuide";
import workerApiReference from "../data/worker-api-reference.json";

const OPENAPI_REFERENCE_URL = "https://console.staging.grupa.ai/docs";

export const API_ENDPOINT_GROUPS = workerApiReference.groups;

export function DocumentationPage() {
  usePageMeta(PAGE_META.documentation);

  const [activeSection, setActiveSection] = useState("introduction");
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    // Scroll to top on mount
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    // Set up IntersectionObserver to track which section is in view
    const observerOptions = {
      root: null,
      rootMargin: '-20% 0px -70% 0px', // Trigger when section is in the middle of viewport
      threshold: 0
    };

    const observerCallback = (entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setActiveSection(entry.target.id);
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);

    // Observe all sections
    const sections = document.querySelectorAll('.docs-section[id]');
    sections.forEach(section => observer.observe(section));

    // Cleanup
    return () => {
      sections.forEach(section => observer.unobserve(section));
    };
  }, []);

  const scrollToSection = (sectionId) => {
    setActiveSection(sectionId);
    setIsMobileMenuOpen(false); // Close menu on selection
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <div className="brand-docs"><Navigation /><div className="docs-container">
      {/* Mobile Header */}
      <div className="docs-mobile-header">
        <Link to="/api" className="docs-logo-mobile">
          <span>GrupaAI Docs</span>
        </Link>
        <button
          className="docs-menu-toggle"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          aria-label="Toggle menu"
        >
          {isMobileMenuOpen ? "✕" : "☰"}
        </button>
      </div>

      {/* Sidebar Navigation */}
      <aside className={`docs-sidebar ${isMobileMenuOpen ? "open" : ""}`}>
        <Link to="/api" className="docs-logo">
          <span>GrupaAI Docs</span>
        </Link>

        <div className="docs-nav-group">
          <div className="docs-nav-title">Getting Started</div>
          <a
            href="#introduction"
            onClick={(e) => { e.preventDefault(); scrollToSection("introduction"); }}
            className={`docs-nav-item ${activeSection === "introduction" ? "active" : ""}`}
          >
            Introduction
          </a>
          <a
            href="#installation"
            onClick={(e) => { e.preventDefault(); scrollToSection("installation"); }}
            className={`docs-nav-item ${activeSection === "installation" ? "active" : ""}`}
          >
            Installation
          </a>
          <a
            href="#authentication"
            onClick={(e) => { e.preventDefault(); scrollToSection("authentication"); }}
            className={`docs-nav-item ${activeSection === "authentication" ? "active" : ""}`}
          >
            Authentication
          </a>
          <a
            href="#quick-start"
            onClick={(e) => { e.preventDefault(); scrollToSection("quick-start"); }}
            className={`docs-nav-item ${activeSection === "quick-start" ? "active" : ""}`}
          >
            Quick Start
          </a>
        </div>

        <div className="docs-nav-group">
          <div className="docs-nav-title">Core Concepts</div>
          <a
            href="#tasks"
            onClick={(e) => { e.preventDefault(); scrollToSection("tasks"); }}
            className={`docs-nav-item ${activeSection === "tasks" ? "active" : ""}`}
          >
            Work Runs
          </a>
          <a
            href="#workers"
            onClick={(e) => { e.preventDefault(); scrollToSection("workers"); }}
            className={`docs-nav-item ${activeSection === "workers" ? "active" : ""}`}
          >
            Workers
          </a>
          <a
            href="#worker-continuity"
            onClick={(e) => { e.preventDefault(); scrollToSection("worker-continuity"); }}
            className={`docs-nav-item ${activeSection === "worker-continuity" ? "active" : ""}`}
          >
            Persistent Workers &amp; Follow-up
          </a>
          <a
            href="#worker-automations"
            onClick={(e) => { e.preventDefault(); scrollToSection("worker-automations"); }}
            className={`docs-nav-item ${activeSection === "worker-automations" ? "active" : ""}`}
          >
            Schedules &amp; Queued Work
          </a>
          <a
            href="#saved-access"
            onClick={(e) => { e.preventDefault(); scrollToSection("saved-access"); }}
            className={`docs-nav-item ${activeSection === "saved-access" ? "active" : ""}`}
          >
            Vault &amp; Saved Access
          </a>
          <a
            href="#client-tools"
            onClick={(e) => { e.preventDefault(); scrollToSection("client-tools"); }}
            className={`docs-nav-item ${activeSection === "client-tools" ? "active" : ""}`}
          >
            Application Tools &amp; Deliverables
          </a>
          <a
            href="#agent-clients"
            onClick={(e) => { e.preventDefault(); scrollToSection("agent-clients"); }}
            className={`docs-nav-item ${activeSection === "agent-clients" ? "active" : ""}`}
          >
            Claude Code, CLI &amp; MCP
          </a>
          <a
            href="#local-browser"
            onClick={(e) => { e.preventDefault(); scrollToSection("local-browser"); }}
            className={`docs-nav-item ${activeSection === "local-browser" ? "active" : ""}`}
          >
            Local Browser Pilot
          </a>
          <a
            href="#credentials"
            onClick={(e) => { e.preventDefault(); scrollToSection("credentials"); }}
            className={`docs-nav-item ${activeSection === "credentials" ? "active" : ""}`}
          >
            Resources
          </a>
          <a
            href="#webhooks"
            onClick={(e) => { e.preventDefault(); scrollToSection("webhooks"); }}
            className={`docs-nav-item ${activeSection === "webhooks" ? "active" : ""}`}
          >
            Webhooks
          </a>
          <a
            href="#approvals"
            onClick={(e) => { e.preventDefault(); scrollToSection("approvals"); }}
            className={`docs-nav-item ${activeSection === "approvals" ? "active" : ""}`}
          >
            Approvals
          </a>
          <a
            href="#orchestration"
            onClick={(e) => { e.preventDefault(); scrollToSection("orchestration"); }}
            className={`docs-nav-item ${activeSection === "orchestration" ? "active" : ""}`}
          >
            Auto Topology
          </a>
          <a
            href="#teams"
            onClick={(e) => { e.preventDefault(); scrollToSection("teams"); }}
            className={`docs-nav-item ${activeSection === "teams" ? "active" : ""}`}
          >
            Worker Teams
          </a>
          <a
            href="#tools-integration"
            onClick={(e) => { e.preventDefault(); scrollToSection("tools-integration"); }}
            className={`docs-nav-item ${activeSection === "tools-integration" ? "active" : ""}`}
          >
            Tools & Integration
          </a>
          <a
            href="#budgeting"
            onClick={(e) => { e.preventDefault(); scrollToSection("budgeting"); }}
            className={`docs-nav-item ${activeSection === "budgeting" ? "active" : ""}`}
          >
            Budget & Controls
          </a>
          <a
            href="#verification"
            onClick={(e) => { e.preventDefault(); scrollToSection("verification"); }}
            className={`docs-nav-item ${activeSection === "verification" ? "active" : ""}`}
          >
            Verification & Proof
          </a>
          <a
            href="#collaboration"
            onClick={(e) => { e.preventDefault(); scrollToSection("collaboration"); }}
            className={`docs-nav-item ${activeSection === "collaboration" ? "active" : ""}`}
          >
            Pair & Teach
          </a>
          <a
            href="#memory"
            onClick={(e) => { e.preventDefault(); scrollToSection("memory"); }}
            className={`docs-nav-item ${activeSection === "memory" ? "active" : ""}`}
          >
            Memory & Learning
          </a>
        </div>

        <div className="docs-nav-group">
          <div className="docs-nav-title">Reference</div>
          <a
            href="#rest-reference"
            onClick={(e) => { e.preventDefault(); scrollToSection("rest-reference"); }}
            className={`docs-nav-item ${activeSection === "rest-reference" ? "active" : ""}`}
          >
            REST Endpoints
          </a>
          <a
            href="#api-reference"
            onClick={(e) => { e.preventDefault(); scrollToSection("api-reference"); }}
            className={`docs-nav-item ${activeSection === "api-reference" ? "active" : ""}`}
          >
            SDK Reference
          </a>
          <a
            href="#types"
            onClick={(e) => { e.preventDefault(); scrollToSection("types"); }}
            className={`docs-nav-item ${activeSection === "types" ? "active" : ""}`}
          >
            Type Definitions
          </a>
          <a
            href="#production"
            onClick={(e) => { e.preventDefault(); scrollToSection("production"); }}
            className={`docs-nav-item ${activeSection === "production" ? "active" : ""}`}
          >
            Production Essentials
          </a>
          <a
            href="#testing"
            onClick={(e) => { e.preventDefault(); scrollToSection("testing"); }}
            className={`docs-nav-item ${activeSection === "testing" ? "active" : ""}`}
          >
            Testing
          </a>
          <a
            href="#error-handling"
            onClick={(e) => { e.preventDefault(); scrollToSection("error-handling"); }}
            className={`docs-nav-item ${activeSection === "error-handling" ? "active" : ""}`}
          >
            Error Handling
          </a>
          <a
            href="#security"
            onClick={(e) => { e.preventDefault(); scrollToSection("security"); }}
            className={`docs-nav-item ${activeSection === "security" ? "active" : ""}`}
          >
            Security
          </a>
          <a
            href="#examples"
            onClick={(e) => { e.preventDefault(); scrollToSection("examples"); }}
            className={`docs-nav-item ${activeSection === "examples" ? "active" : ""}`}
          >
            Examples
          </a>
          <a
            href="#use-cases"
            onClick={(e) => { e.preventDefault(); scrollToSection("use-cases"); }}
            className={`docs-nav-item ${activeSection === "use-cases" ? "active" : ""}`}
          >
            Use Cases
          </a>
          <a
            href="#billing"
            onClick={(e) => { e.preventDefault(); scrollToSection("billing"); }}
            className={`docs-nav-item ${activeSection === "billing" ? "active" : ""}`}
          >
            Billing
          </a>
          <a
            href={OPENAPI_REFERENCE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="docs-nav-item"
          >
            View The Swagger Docs ↗
          </a>
        </div>

        <Link to="/api" className="docs-nav-item back-home">
          ← Back to Workers API
        </Link>
      </aside>

      {/* Overlay for mobile */}
      {isMobileMenuOpen && (
        <button
          className="docs-mobile-overlay"
          onClick={() => setIsMobileMenuOpen(false)}
          aria-label="Close menu"
        />
      )}

      {/* Main Content */}
      <main className="docs-content" id="main-content" tabIndex="-1">
        <header className="docs-header">
          <h1 className="docs-title">Workers API Documentation</h1>
          <p className="docs-subtitle">
            The cloud for autonomous workers. Start durable runs, attach resources
            and guardrails, and retrieve outcomes you can verify.
          </p>
          <div className="docs-header-actions">
            <a href={OPENAPI_REFERENCE_URL} target="_blank" rel="noopener noreferrer">
              View The Swagger Docs ↗
            </a>
          </div>
          <p className="docs-contract-note">
            Reviewed October 27, 2026 against <strong>GrupaAI Worker API 1.0.0</strong>.
            The Swagger reference is the canonical staging contract; production
            availability can differ until a backend release is promoted.
          </p>
        </header>

        <section id="introduction" className="docs-section">
          <h2 className="docs-section-title">Introduction</h2>
          <p className="docs-text">
            GrupaAI gives developers durable digital humanoid workers as cloud
            infrastructure. A worker can operate websites, portals, SaaS apps,
            credentials, cards, files, tools, and budgets to complete real work.
          </p>
          <p className="docs-text">
            Use <code>workers.run</code> for on-demand work,{" "}
            <code>workers.create</code> for long-lived workers, and{" "}
            <code>worker.mode</code> to choose one worker, a persistent worker,
            or a team. Use <code>worker.availability</code> for on-demand,
            part-time, full-time, or 24/7. The primitive is simple:{" "}
            <code>worker + work + resources + guardrails -&gt; outcome</code>.
          </p>
          <p className="docs-text">
            <code>work</code> maps to how businesses assign people today — not a single browser step:
          </p>
          <ul className="docs-list">
            <li><strong>Job ticket</strong> — one case or order (prior auth #4412, customer order #9921)</li>
            <li><strong>Shift assignment</strong> — one account or queue for a block of time (BPO night shift, Monday RevOps reconcile)</li>
            <li><strong>Function brief</strong> — a team objective for a period (grow marketplace liquidity this week)</li>
          </ul>
          <div className="docs-callout">
            <div className="docs-callout-title">🚀 Early Access</div>
            <p className="docs-callout-text">
              We are currently in private beta. You will need an API key to use
              the SDK. <Link to="/api">Request access here</Link>.
            </p>
          </div>
        </section>

        <section id="installation" className="docs-section">
          <h2 className="docs-section-title">Installation</h2>
          <p className="docs-text">
            The REST API requires no SDK. Use any HTTP client against the base URL
            supplied with your access. The generated staging reference uses{" "}
            <code>https://api.staging.grupa.ai</code>.
          </p>
          <div className="docs-callout">
            <div className="docs-callout-title">Private-beta JavaScript SDK</div>
            <p className="docs-callout-text">
              <code>@grupaai/sdk</code> is not distributed through the public npm
              registry. Run the command below only if your access instructions include
              private-registry credentials. Otherwise, use the REST examples on this page.
            </p>
          </div>
          <p className="docs-text">
            For approved SDK users:
          </p>
          <div className="docs-code-block">
            <div className="docs-code-header">
              <span className="docs-lang">BASH</span>
            </div>
            <div className="docs-code-content">
              npm install @grupaai/sdk
            </div>
          </div>
        </section>

        <section id="authentication" className="docs-section">
          <h2 className="docs-section-title">Authentication</h2>
          <p className="docs-text">
            Worker API requests use HTTP Bearer authentication. The Stripe billing webhook uses its own signature verification. Keep API keys server-side
            and load them from an environment variable.
          </p>
          <div className="docs-code-block">
            <div className="docs-code-header">
              <span className="docs-lang">HTTP</span>
            </div>
            <div className="docs-code-content">
              <div>Authorization: Bearer $GRUPA_API_KEY</div>
            </div>
          </div>
          <p className="docs-text">
            In Swagger Docs, select <strong>Authorize</strong> and
            paste the full <code>grupa_live_…</code> or <code>grupa_test_…</code> key without a <code>Bearer</code> prefix; the console adds it. Create keys in Console → Settings → API Keys.
            Approved SDK users can initialize the client as follows:
          </p>
          <div className="docs-code-block">
            <div className="docs-code-header">
              <span className="docs-lang">JAVASCRIPT</span>
            </div>
            <div className="docs-code-content">
              <div><span className="docs-token-keyword">import</span> <span className="docs-token-operator">{`{`}</span> GrupaAI <span className="docs-token-operator">{`}`}</span> <span className="docs-token-keyword">from</span> <span className="docs-token-string">&apos;@grupaai/sdk&apos;</span>;</div>
              <br />
              <div><span className="docs-token-keyword">const</span> client <span className="docs-token-operator">=</span> <span className="docs-token-keyword">new</span> <span className="docs-token-class">GrupaAI</span>(process.env.GRUPA_API_KEY);</div>
            </div>
          </div>
        </section>

        <section id="quick-start" className="docs-section">
          <h2 className="docs-section-title">Quick Start</h2>
          <p className="docs-text">
            Start a durable worker run with a single REST request. The API accepts
            the run and returns <code>202 Accepted</code> with a{" "}
            <code>WorkerRun</code>.
          </p>
          <div className="docs-code-block">
            <div className="docs-code-header">
              <span className="docs-lang">CURL</span>
            </div>
            <div className="docs-code-content">
              <div>curl -X POST &quot;$GRUPA_API_BASE_URL/v1/workers/run&quot; \</div>
              <div>&nbsp;&nbsp;-H &quot;Authorization: Bearer $GRUPA_API_KEY&quot; \</div>
              <div>&nbsp;&nbsp;-H &quot;Content-Type: application/json&quot; \</div>
              <div>&nbsp;&nbsp;-H &quot;Idempotency-Key: marketplace-run-8842&quot; \</div>
              <div>&nbsp;&nbsp;-d &apos;{`{`}</div>
              <div>&nbsp;&nbsp;&nbsp;&nbsp;&quot;work&quot;: &quot;Grow liquidity on our AI talent marketplace tonight&quot;,</div>
              <div>&nbsp;&nbsp;&nbsp;&nbsp;&quot;worker&quot;: {`{`} &quot;mode&quot;: &quot;team&quot;, &quot;availability&quot;: &quot;24_7&quot; {`}`},</div>
              <div>&nbsp;&nbsp;&nbsp;&nbsp;&quot;resources&quot;: {`{`} &quot;credentials&quot;: &quot;vault_marketplace_ops&quot;, &quot;budget&quot;: 300 {`}`},</div>
              <div>&nbsp;&nbsp;&nbsp;&nbsp;&quot;guardrails&quot;: {`{`} &quot;approvals&quot;: [&quot;messages&quot;, &quot;external_submit&quot;] {`}`},</div>
              <div>&nbsp;&nbsp;&nbsp;&nbsp;&quot;outcome&quot;: &quot;Return supply added, demand qualified, and matches made&quot;</div>
              <div>&nbsp;&nbsp;{`}`}&apos;</div>
            </div>
          </div>
          <h3 className="docs-subtitle">Private-beta SDK equivalent</h3>
          <p className="docs-text">
            If your account includes SDK access, the equivalent call is:
          </p>
          <div className="docs-code-block">
            <div className="docs-code-header">
              <span className="docs-lang">JAVASCRIPT</span>
            </div>
            <div className="docs-code-content">
              <div><span className="docs-token-keyword">import</span> <span className="docs-token-operator">{`{`}</span> GrupaAI <span className="docs-token-operator">{`}`}</span> <span className="docs-token-keyword">from</span> <span className="docs-token-string">&apos;@grupaai/sdk&apos;</span>;</div>
              <br />
              <div><span className="docs-token-keyword">const</span> client <span className="docs-token-operator">=</span> <span className="docs-token-keyword">new</span> <span className="docs-token-class">GrupaAI</span>(process.env.GRUPA_API_KEY);</div>
              <br />
              <div><span className="docs-token-keyword">const</span> run <span className="docs-token-operator">=</span> <span className="docs-token-keyword">await</span> client.workers.<span className="docs-token-function">run</span>(<span className="docs-token-operator">{`{`}</span></div>
              <div className="docs-code-line docs-ci-1">worker: <span className="docs-token-operator">{`{`}</span></div>
              <div className="docs-code-line docs-ci-2">role: <span className="docs-token-string">&quot;Marketplace Operator&quot;</span>,</div>
              <div className="docs-code-line docs-ci-2">mode: <span className="docs-token-string">&quot;team&quot;</span>,</div>
              <div className="docs-code-line docs-ci-2">availability: <span className="docs-token-string">&quot;24_7&quot;</span>,</div>
              <div className="docs-code-line docs-ci-1"><span className="docs-token-operator">{`},`}</span></div>
              <div className="docs-code-line docs-ci-1">work: <span className="docs-token-string">&quot;Grow liquidity on our AI talent marketplace tonight&quot;</span>,</div>
              <div className="docs-code-line docs-ci-1">resources: <span className="docs-token-operator">{`{`}</span></div>
              <div className="docs-code-line docs-ci-2">credentials: <span className="docs-token-string">&quot;vault_marketplace_ops&quot;</span>,</div>
              <div className="docs-code-line docs-ci-2">budget: <span className="docs-token-number">300</span>,</div>
              <div className="docs-code-line docs-ci-1"><span className="docs-token-operator">{`},`}</span></div>
              <div className="docs-code-line docs-ci-1">guardrails: <span className="docs-token-operator">{`{`}</span></div>
              <div className="docs-code-line docs-ci-2">approvals: [<span className="docs-token-string">&quot;messages&quot;</span>, <span className="docs-token-string">&quot;external_submit&quot;</span>],</div>
              <div className="docs-code-line docs-ci-1"><span className="docs-token-operator">{`},`}</span></div>
              <div className="docs-code-line docs-ci-1">outcome: <span className="docs-token-string">&quot;Return supply added, demand qualified, matches made&quot;</span>,</div>
              <div className="docs-code-line docs-ci-1">webhook: <span className="docs-token-string">&quot;https://you.com/grupa/events&quot;</span>,</div>
              <div><span className="docs-token-operator">{`}`}</span>);</div>
            </div>
          </div>
          <div className="docs-callout">
            <div className="docs-callout-title">What happens next</div>
            <p className="docs-callout-text">
              Poll <code>GET /v1/runs/{"{runId}"}</code>, resume the durable SSE
              stream at <code>GET /v1/runs/{"{runId}"}/events</code>, or receive a
              signed <code>worker.completed</code> webhook. The completed run can
              include the outcome, cost, verification, trace, recordings, and proof
              artifacts.
            </p>
          </div>
        </section>

        <section id="tasks" className="docs-section">
          <h2 className="docs-section-title">Work Runs</h2>
          <p className="docs-text">
            A <strong>Work Run</strong> is one assignment given to one worker or
            a team of workers — the same unit a manager would assign to a person:
            a job ticket, shift assignment, or function brief. Every run has an outcome contract, status,
            trace, metadata, resources, and optional guardrails.
          </p>
          <ul className="docs-list">
            <li><strong>worker</strong> - role, topology mode, and availability.</li>
            <li><strong>work</strong> - required. Natural-language description of the job to complete.</li>
            <li><strong>resources</strong> - optional. Credentials, card, files, tools, budget, and context.</li>
            <li><strong>guardrails</strong> - optional. Approvals, spend limits, submit gates, and timeouts.</li>
            <li><strong>outcome</strong> - optional. What should be returned when the work is finished.</li>
            <li><strong>output</strong> - optional. Recording, structured deliverable contract, and PII masking controls.</li>
            <li><strong>retainContext</strong> - optional. Preserve compact context for a later continuation.</li>
            <li><strong>scheduleAt</strong> - optional. Future dispatch time, up to 365 days ahead.</li>
            <li><strong>idempotencyKey</strong> - optional SDK field mapped to the <code>Idempotency-Key</code> header.</li>
            <li><strong>webhook</strong> - optional. URL for worker lifecycle events.</li>
            <li><strong>metadata</strong> - optional. Your IDs for correlation.</li>
          </ul>
          <div className="docs-code-block">
            <div className="docs-code-header">
              <span className="docs-lang">JAVASCRIPT</span>
            </div>
            <div className="docs-code-content">
              <div><span className="docs-token-comment">{/* Poll or use webhooks */}</span></div>
              <div><span className="docs-token-keyword">const</span> run <span className="docs-token-operator">=</span> <span className="docs-token-keyword">await</span> client.workers.<span className="docs-token-function">retrieveRun</span>(runId);</div>
              <div>console.<span className="docs-token-function">log</span>(run.status, run.outcome, run.outputCheckpoint, run.trace);</div>
            </div>
          </div>
          <p className="docs-text">
            Run statuses are <code>pending</code>, <code>running</code>,{" "}
            <code>needs_approval</code>, <code>stuck</code>, <code>verifying</code>,{" "}
            <code>completed</code>, <code>failed</code>, and <code>canceled</code>.
          </p>
        </section>

        <section id="workers" className="docs-section">
          <h2 className="docs-section-title">Workers</h2>
          <p className="docs-text">
            Workers are the core primitive. Choose how they are deployed with{" "}
            <code>worker.mode</code>, then choose when they run with{" "}
            <code>worker.availability</code>.
          </p>
          <ul className="docs-list">
            <li><strong>mode: &quot;single&quot;</strong> - one worker, one outcome.</li>
            <li><strong>mode: &quot;persistent&quot;</strong> - long-lived worker for repeat assignments.</li>
            <li><strong>mode: &quot;team&quot; or &quot;auto&quot;</strong> - run multiple roles or let GrupaAI choose topology.</li>
            <li><strong>availability: &quot;on_demand&quot;</strong> - spin up for one job, then done.</li>
            <li><strong>availability: &quot;part_time&quot;</strong> - scheduled repeat work for specific windows.</li>
            <li><strong>availability: &quot;full_time&quot;</strong> - active through business hours for a function.</li>
            <li><strong>availability: &quot;24_7&quot;</strong> - eligible for assignments around the clock; idle identity does not reserve a desktop.</li>
          </ul>
          <div className="docs-code-block">
            <div className="docs-code-header">
              <span className="docs-lang">JAVASCRIPT</span>
            </div>
            <div className="docs-code-content">
              <div><span className="docs-token-keyword">const</span> worker <span className="docs-token-operator">=</span> <span className="docs-token-keyword">await</span> client.workers.<span className="docs-token-function">create</span>(<span className="docs-token-operator">{`{`}</span></div>
              <div className="docs-code-line docs-ci-1">name: <span className="docs-token-string">&quot;Night Shift Ops&quot;</span>,</div>
              <div className="docs-code-line docs-ci-1">mode: <span className="docs-token-string">&quot;persistent&quot;</span>,</div>
              <div className="docs-code-line docs-ci-1">availability: <span className="docs-token-string">&quot;24_7&quot;</span>,</div>
              <div className="docs-code-line docs-ci-1">resources: <span className="docs-token-operator">{`{`}</span></div>
              <div className="docs-code-line docs-ci-2">credentials: <span className="docs-token-string">&quot;vault_vendor_prod&quot;</span>,</div>
              <div className="docs-code-line docs-ci-1"><span className="docs-token-operator">{`},`}</span></div>
              <div><span className="docs-token-operator">{`}`}</span>);</div>
              <br />
              <div><span className="docs-token-keyword">await</span> worker.<span className="docs-token-function">assign</span>(<span className="docs-token-operator">{`{`}</span></div>
              <div className="docs-code-line docs-ci-1">work: <span className="docs-token-string">&quot;Process overnight ticket queue in vendor portal&quot;</span>,</div>
              <div><span className="docs-token-operator">{`}`}</span>);</div>
            </div>
          </div>
        </section>

        <WorkerContinuityGuide />
        <AgentClientsGuide />

        <section id="credentials" className="docs-section">
          <h2 className="docs-section-title">Resources</h2>
          <p className="docs-text">
            Resources are what the worker can use: portal logins, payment
            methods, files, built-in tools, MCP servers, custom APIs, budgets,
            and scoped secrets. Reference credential IDs, zero-custody connection
            IDs, agent identities, card IDs, and uploaded file IDs instead of
            embedding raw secrets or file contents in a run.
          </p>
          <div className="docs-code-block">
            <div className="docs-code-header">
              <span className="docs-lang">JAVASCRIPT</span>
            </div>
            <div className="docs-code-content">
              <div>resources: <span className="docs-token-operator">{`{`}</span></div>
              <div>&nbsp;&nbsp;credentials: <span className="docs-token-string">&quot;vault_healthcare_prod&quot;</span>,</div>
              <div>&nbsp;&nbsp;credentialConnections: [<span className="docs-token-string">&quot;ccn_payer_browser&quot;</span>],</div>
              <div>&nbsp;&nbsp;agentIdentity: <span className="docs-token-string">&quot;ops@your-company.com&quot;</span>,</div>
              <div>&nbsp;&nbsp;card: <span className="docs-token-string">&quot;card_vault_xxx&quot;</span>,</div>
              <div>&nbsp;&nbsp;budget: <span className="docs-token-number">250</span>,</div>
              <div>&nbsp;&nbsp;files: [<span className="docs-token-string">&quot;file_0123456789abcdef0123456789abcdef&quot;</span>],</div>
              <div>&nbsp;&nbsp;scopes: [<span className="docs-token-string">&quot;payer_portal&quot;</span>, <span className="docs-token-string">&quot;prior_auth&quot;</span>]</div>
              <div><span className="docs-token-operator">{`}`}</span></div>
            </div>
          </div>
          <h3 className="docs-subtitle">Files</h3>
          <p className="docs-text">
            Upload immutable CSV, XLS/XLSX, or binary inputs up to 25 MiB using{" "}
            <code>POST /v1/files</code> and the required{" "}
            <code>X-GrupaAI-File-Name</code> header. The returned opaque{" "}
            <code>file_...</code> ID is safe to place in{" "}
            <code>resources.files</code>.
          </p>
          <h3 className="docs-subtitle">Credentials</h3>
          <p className="docs-text">
            <code>POST /v1/credentials</code> stores encrypted login, API-token,
            OAuth, or HTTP-header material and returns metadata without secret
            values. For zero-custody access, use credential broker manifests and
            credential connections; runs receive only ready connection IDs or an
            independently brokered <code>agentIdentity</code>.
          </p>
          <div className="docs-callout">
            <div className="docs-callout-title">🔐 Early Access</div>
            <p className="docs-callout-text">
              The API exposes credential, credential-connection, and file endpoints,
              but production provisioning remains access-controlled.{" "}
              <Link to="/api">Request API access</Link> before connecting real systems
              or payment resources.
            </p>
          </div>
        </section>

        <section id="webhooks" className="docs-section">
          <h2 className="docs-section-title">Webhooks &amp; Lifecycle</h2>
          <p className="docs-text">
            Register an HTTPS webhook URL on a run or create an account-level
            subscription with <code>POST /v1/webhook-subscriptions</code>. Run
            submission returns <code>webhookSigningSecret</code> only when a
            per-run webhook is configured. Store it immediately.
          </p>
          <ul className="docs-list">
            <li><code>worker.started</code> - worker began execution</li>
            <li><code>worker.needs_approval</code> - paused for a human gate (payments, submits)</li>
            <li><code>worker.verifying</code> - outcome produced; running the independent check</li>
            <li><code>worker.stuck</code> - no progress / blocked (e.g. rate-limited); the worker paused and reported instead of looping</li>
            <li><code>worker.completed</code> - success with outcome, cost, verification, and trace URL</li>
            <li><code>worker.failed</code> - terminal failure with error detail</li>
          </ul>
          <p className="docs-text">
            Every delivery is signed. Verify the <code>X-Grupa-Signature</code> header
            before trusting a payload (see <a href="#production">Production Essentials → Webhook Signing</a>), and treat unsigned requests as hostile.
          </p>
          <div className="docs-code-block">
            <div className="docs-code-header">
              <span className="docs-lang">JSON</span>
            </div>
            <div className="docs-code-content">
              <div><span className="docs-token-operator">{`{`}</span></div>
              <div>&nbsp;&nbsp;<span className="docs-token-string">&quot;type&quot;</span>: <span className="docs-token-string">&quot;worker.completed&quot;</span>,</div>
              <div>&nbsp;&nbsp;<span className="docs-token-string">&quot;run_id&quot;</span>: <span className="docs-token-string">&quot;run_abc123&quot;</span>,</div>
              <div>&nbsp;&nbsp;<span className="docs-token-string">&quot;event_id&quot;</span>: <span className="docs-token-string">&quot;evt_abc123&quot;</span>,</div>
              <div>&nbsp;&nbsp;<span className="docs-token-string">&quot;timestamp&quot;</span>: <span className="docs-token-string">&quot;2026-09-21T20:15:00Z&quot;</span>,</div>
              <div>&nbsp;&nbsp;<span className="docs-token-string">&quot;outcome&quot;</span>: <span className="docs-token-operator">{`{`}</span> ... <span className="docs-token-operator">{`}`}</span>,</div>
              <div>&nbsp;&nbsp;<span className="docs-token-string">&quot;cost&quot;</span>: <span className="docs-token-operator">{`{`}</span> <span className="docs-token-string">&quot;total_usd&quot;</span>: <span className="docs-token-number">0.42</span> <span className="docs-token-operator">{`}`}</span>,</div>
              <div>&nbsp;&nbsp;<span className="docs-token-string">&quot;verification&quot;</span>: <span className="docs-token-operator">{`{`}</span> <span className="docs-token-string">&quot;status&quot;</span>: <span className="docs-token-string">&quot;passed&quot;</span> <span className="docs-token-operator">{`}`}</span>,</div>
              <div>&nbsp;&nbsp;<span className="docs-token-string">&quot;trace_url&quot;</span>: <span className="docs-token-string">&quot;/v1/runs/run_abc123/trace&quot;</span></div>
              <div><span className="docs-token-operator">{`}`}</span></div>
            </div>
          </div>
        </section>

        <section id="orchestration" className="docs-section">
          <h2 className="docs-section-title">Auto Topology</h2>
          <p className="docs-text">
            If you prefer not to design worker topology, use{" "}
            <code>worker.mode: &quot;auto&quot;</code> on <code>workers.run</code>. The platform
            decides worker count, team roles, and sequencing from your goal.
          </p>
          <div className="docs-code-block">
            <div className="docs-code-header">
              <span className="docs-lang">JAVASCRIPT</span>
            </div>
            <div className="docs-code-content">
              <div><span className="docs-token-keyword">const</span> run <span className="docs-token-operator">=</span> <span className="docs-token-keyword">await</span> client.workers.<span className="docs-token-function">run</span>(<span className="docs-token-operator">{`{`}</span></div>
              <div>&nbsp;&nbsp;work: <span className="docs-token-string">&quot;Research new AI infrastructure companies and update our CRM&quot;</span>,</div>
              <div>&nbsp;&nbsp;worker: <span className="docs-token-operator">{`{`}</span> mode: <span className="docs-token-string">&quot;auto&quot;</span> <span className="docs-token-operator">{`}`}</span>,</div>
              <div>&nbsp;&nbsp;resources: <span className="docs-token-operator">{`{`}</span> budget: <span className="docs-token-number">50</span> <span className="docs-token-operator">{`}`}</span></div>
              <div><span className="docs-token-operator">{`}`}</span>);</div>
            </div>
          </div>
        </section>

        <section id="teams" className="docs-section">
          <h2 className="docs-section-title">Worker Teams &amp; Visibility</h2>
          <p className="docs-text">
            When you run a team, GrupaAI provisions specialized workers for the
            work: researchers, operators, reviewers, schedulers, or custom
            roles you define.
          </p>
          <p className="docs-text">
            <strong>Full Visibility:</strong> Unlike black-box solutions,
            GrupaAI provides a real-time trace of the entire worker team&apos;s activity.
            The <code>onUpdate</code> callback receives events from all workers,
            allowing you to show the user exactly what is happening.
          </p>
          <div className="docs-code-block">
            <div className="docs-code-header">
              <span className="docs-lang">JAVASCRIPT</span>
            </div>
            <div className="docs-code-content">
              <div>onUpdate: (trace) <span className="docs-token-operator">=&gt;</span> <span className="docs-token-operator">{`{`}</span></div>
              <div>&nbsp;&nbsp;<span className="docs-token-keyword">if</span> (trace.type <span className="docs-token-operator">===</span> <span className="docs-token-string">&quot;worker_thinking&quot;</span>) <span className="docs-token-operator">{`{`}</span></div>
              <div>&nbsp;&nbsp;&nbsp;&nbsp;console.<span className="docs-token-function">log</span>(<span className="docs-token-string">`Worker is thinking...`</span>);</div>
              <div>&nbsp;&nbsp;<span className="docs-token-operator">{`}`}</span> <span className="docs-token-keyword">else</span> <span className="docs-token-keyword">if</span> (trace.type <span className="docs-token-operator">===</span> <span className="docs-token-string">&quot;tool_used&quot;</span>) <span className="docs-token-operator">{`{`}</span></div>
              <div>&nbsp;&nbsp;&nbsp;&nbsp;console.<span className="docs-token-function">log</span>(<span className="docs-token-string">`Used tool successfully`</span>);</div>
              <div>&nbsp;&nbsp;<span className="docs-token-operator">{`}`}</span></div>
              <div><span className="docs-token-operator">{`}`}</span></div>
            </div>
          </div>
        </section>

        <section id="tools-integration" className="docs-section">
          <h2 className="docs-section-title">Tools & Integration</h2>
          <p className="docs-text">
            Attach tools inside <code>resources</code>: built-in tools, MCP
            servers, or custom API endpoints.
          </p>

          <h3 className="docs-subtitle">Built-in Tools</h3>
          <p className="docs-text">
            These tools are always available. Pass them in{" "}
            <code>resources.tools</code>:
          </p>
          <ul className="docs-list">
            <li><strong>web_search</strong> - Search the web for information</li>
            <li><strong>email_send</strong> - Send emails with custom content</li>
            <li><strong>calendar_invite</strong> - Create and manage calendar events</li>
            <li><strong>file_upload</strong> - Upload and process files</li>
            <li><strong>code_execution</strong> - Execute code in secure environments</li>
          </ul>

          <h3 className="docs-subtitle">MCP (Model Context Protocol) Servers</h3>
          <p className="docs-text">
            Connect standard data sources with simple connection strings:
          </p>
          <div className="docs-code-block">
            <div className="docs-code-header">
              <span className="docs-lang">JAVASCRIPT</span>
            </div>
            <div className="docs-code-content">
              <div><span className="docs-token-keyword">const</span> run <span className="docs-token-operator">=</span> <span className="docs-token-keyword">await</span> client.workers.<span className="docs-token-function">run</span>(<span className="docs-token-operator">{`{`}</span></div>
              <div className="docs-code-line docs-ci-1">work: <span className="docs-token-string">&quot;Analyze customer data and generate insights&quot;</span>,</div>
              <div className="docs-code-line docs-ci-1">resources: <span className="docs-token-operator">{`{`}</span></div>
              <div className="docs-code-line docs-ci-2">mcp: <span className="docs-token-operator">{`{`}</span></div>
              <div className="docs-code-line docs-ci-3">postgres: <span className="docs-token-string">&quot;postgresql://user:pass@host/crm_db&quot;</span>,</div>
              <div className="docs-code-line docs-ci-3">notion: <span className="docs-token-string">&quot;notion://workspace-id&quot;</span>,</div>
              <div className="docs-code-line docs-ci-3">google_drive: <span className="docs-token-string">&quot;gdrive://folder-id&quot;</span>,</div>
              <div className="docs-code-line docs-ci-2"><span className="docs-token-operator">{`},`}</span></div>
              <div className="docs-code-line docs-ci-1"><span className="docs-token-operator">{`},`}</span></div>
              <div><span className="docs-token-operator">{`}`}</span>);</div>
            </div>
          </div>

          <h3 className="docs-subtitle">Custom Tools</h3>
          <p className="docs-text">
            Connect your internal APIs and proprietary systems:
          </p>
          <div className="docs-code-block">
            <div className="docs-code-header">
              <span className="docs-lang">JAVASCRIPT</span>
            </div>
            <div className="docs-code-content">
              <div><span className="docs-token-keyword">const</span> run <span className="docs-token-operator">=</span> <span className="docs-token-keyword">await</span> client.workers.<span className="docs-token-function">run</span>(<span className="docs-token-operator">{`{`}</span></div>
              <div className="docs-code-line docs-ci-1">work: <span className="docs-token-string">&quot;Update CRM and billing system&quot;</span>,</div>
              <div className="docs-code-line docs-ci-1">resources: <span className="docs-token-operator">{`{`}</span></div>
              <div className="docs-code-line docs-ci-2">customTools: <span className="docs-token-operator">[</span><span className="docs-token-operator">{`{`}</span></div>
              <div className="docs-code-line docs-ci-3">name: <span className="docs-token-string">&quot;company_crm&quot;</span>,</div>
              <div className="docs-code-line docs-ci-3">endpoint: <span className="docs-token-string">&quot;https://api.company.com/leads&quot;</span>,</div>
              <div className="docs-code-line docs-ci-3">auth: process.env.CRM_API_KEY,</div>
              <div className="docs-code-line docs-ci-2"><span className="docs-token-operator">{`}`}</span><span className="docs-token-operator">],</span></div>
              <div className="docs-code-line docs-ci-1"><span className="docs-token-operator">{`},`}</span></div>
              <div><span className="docs-token-operator">{`}`}</span>);</div>
            </div>
          </div>
        </section>

        <section id="approvals" className="docs-section">
          <h2 className="docs-section-title">Approvals</h2>
          <p className="docs-text">
            Gate high-stakes actions - payments, form submits, message sends -
            with human-in-the-loop approval. Works on any worker mode.
          </p>
          <div className="docs-callout">
            <div className="docs-callout-title">Safe production default</div>
            <p className="docs-callout-text">
              If <code>guardrails.approvals</code> is omitted, production runs
              require approval for all consequential categories by default:
              messages, external submissions, payments, and trades. Setting{" "}
              <code>preauthorizeConsequential: true</code> explicitly bypasses that
              default and should be used only when authority is established elsewhere.
            </p>
          </div>
          <div className="docs-code-block">
            <div className="docs-code-header">
              <span className="docs-lang">JAVASCRIPT</span>
            </div>
            <div className="docs-code-content">
              <div><span className="docs-token-keyword">const</span> run <span className="docs-token-operator">=</span> <span className="docs-token-keyword">await</span> client.workers.<span className="docs-token-function">run</span>(<span className="docs-token-operator">{`{`}</span></div>
              <div className="docs-code-line docs-ci-1">worker: <span className="docs-token-operator">{`{`}</span></div>
              <div className="docs-code-line docs-ci-2">role: <span className="docs-token-string">&quot;Commerce Operator&quot;</span>,</div>
              <div className="docs-code-line docs-ci-2">mode: <span className="docs-token-string">&quot;persistent&quot;</span>,</div>
              <div className="docs-code-line docs-ci-1"><span className="docs-token-operator">{`},`}</span></div>
              <div className="docs-code-line docs-ci-1">work: <span className="docs-token-string">&quot;Place DoorDash order for customer order #9921&quot;</span>,</div>
              <div className="docs-code-line docs-ci-1">resources: <span className="docs-token-operator">{`{`}</span></div>
              <div className="docs-code-line docs-ci-2">credentials: <span className="docs-token-string">&quot;vault_doordash&quot;</span>,</div>
              <div className="docs-code-line docs-ci-2">card: <span className="docs-token-string">&quot;card_vault_xxx&quot;</span>,</div>
              <div className="docs-code-line docs-ci-2">budget: <span className="docs-token-number">75</span>,</div>
              <div className="docs-code-line docs-ci-1"><span className="docs-token-operator">{`},`}</span></div>
              <div className="docs-code-line docs-ci-1">guardrails: <span className="docs-token-operator">{`{`}</span></div>
              <div className="docs-code-line docs-ci-2">approvals: [<span className="docs-token-string">&quot;payments&quot;</span>],</div>
              <div className="docs-code-line docs-ci-1"><span className="docs-token-operator">{`},`}</span></div>
              <div className="docs-code-line docs-ci-1">webhook: <span className="docs-token-string">&quot;https://you.com/grupa/events&quot;</span>,</div>
              <div><span className="docs-token-operator">{`}`}</span>);</div>
            </div>
          </div>
          <div className="docs-callout">
            <div className="docs-callout-title">🔔 Approval Notifications</div>
            <p className="docs-callout-text">
              When approval is required, the workflow pauses and emits a signed
              event. List approvals at <code>GET /v1/runs/{"{runId}"}/approvals</code>,
              then approve or deny with the approval&apos;s current{" "}
              <code>version</code>. A stale version returns <code>409 Conflict</code>.
              The private-beta SDK may expose the same flow through{" "}
              <code>onApproval</code>.
            </p>
          </div>
          <div className="docs-code-block">
            <div className="docs-code-header">
              <span className="docs-lang">CURL</span>
            </div>
            <div className="docs-code-content">
              <div>curl -X POST &quot;$GRUPA_API_BASE_URL/v1/runs/$RUN_ID/approvals/$APPROVAL_ID/approve&quot; \</div>
              <div>&nbsp;&nbsp;-H &quot;Authorization: Bearer $GRUPA_API_KEY&quot; \</div>
              <div>&nbsp;&nbsp;-H &quot;Content-Type: application/json&quot; \</div>
              <div>&nbsp;&nbsp;-d &apos;{`{`} &quot;version&quot;: 3, &quot;reason&quot;: &quot;Approved by operator&quot; {`}`}&apos;</div>
            </div>
          </div>
        </section>

        <section id="budgeting" className="docs-section">
          <h2 className="docs-section-title">Budget & Controls</h2>
          <p className="docs-text">
            Budgets and limits are the outer guardrail — but a worker also stops on its own
            judgment. Like a competent person, it continuously checks whether it is actually
            making progress. If it hits a wall (a rate limit, a dead control, repeated no-op
            actions), it does not loop or invent busywork: it enters <code>stuck</code>, emits a{" "}
            <code>worker.stuck</code> event explaining what blocked it, and waits for input
            instead of burning budget. Attach <code>budget</code> and <code>guardrails.timeout</code>{" "}
            as the hard backstop under that judgment.
          </p>
          <ul className="docs-list">
            <li><strong>resources.budget:</strong> Maximum external purchase spend in USD; this is separate from service execution charges.</li>
            <li><strong>guardrails.timeout:</strong> Maximum duration in milliseconds.</li>
            <li><strong>guardrails.approvals:</strong> Actions that must pause for human approval.</li>
          </ul>
        </section>

        <section id="rest-reference" className="docs-section">
          <h2 className="docs-section-title">REST Endpoint Reference</h2>
          <p className="docs-text">
            This catalog lists all {API_ENDPOINT_GROUPS.reduce((count, group) => count + group.endpoints.length, 0)} operations in the OpenAPI 1.0.0 staging contract reviewed October 27, 2026. Alias routes are included as published. Access restrictions still apply; inclusion is not a grant of permission.
            The{" "}
            <a
              href={OPENAPI_REFERENCE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="docs-link"
            >
              Swagger Docs
            </a>{" "}
            remains canonical for request bodies, responses, limits, and newly
            promoted endpoints.
          </p>
          <div className="docs-endpoint-groups">
            {API_ENDPOINT_GROUPS.map((group) => (
              <section className="docs-endpoint-group" key={group.name}>
                <h3>{group.name}</h3>
                <p>{group.description}</p>
                <div className="docs-endpoint-list">
                  {group.endpoints.map(([method, path, summary]) => (
                    <div className="docs-endpoint" key={`${method}-${path}`}>
                      <span className={`docs-method docs-method--${method.toLowerCase()}`}>
                        {method}
                      </span>
                      <code>{path}</code>
                      <span>{summary}</span>
                    </div>
                  ))}
                </div>
              </section>
            ))}
          </div>
        </section>

        <section id="api-reference" className="docs-section">
          <h2 className="docs-section-title">Private-beta SDK Reference</h2>
          <p className="docs-text">
            Convenience methods for accounts that have been granted access to{" "}
            <code>@grupaai/sdk</code>. The REST/OpenAPI contract above is canonical.
          </p>

          <h3 className="docs-subtitle">workers.run(config)</h3>
          <p className="docs-text">
            Start one on-demand worker or a worker team. Returns a run object
            with <code>id</code>, <code>status</code>, and eventually an
            <code> outcome</code>.
          </p>

          <h4 className="docs-subtitle">Parameters</h4>
          <div className="docs-code-block">
            <div className="docs-code-header">
              <span className="docs-lang">TYPESCRIPT</span>
            </div>
            <div className="docs-code-content">
              <div><span className="docs-token-keyword">interface</span> <span className="docs-token-class">WorkerRunConfig</span> <span className="docs-token-operator">{`{`}</span></div>
              <div>&nbsp;&nbsp;retainContext?: boolean;</div>
              <div>&nbsp;&nbsp;worker?: <span className="docs-token-operator">{`{`}</span></div>
              <div>&nbsp;&nbsp;&nbsp;&nbsp;instructions?: string; skills?: WorkerSkill[]; persistent?: boolean;</div>
              <div>&nbsp;&nbsp;&nbsp;&nbsp;role?: string;                          <span className="docs-token-comment">{/* Optional — inferred from `work` if omitted */}</span></div>
              <div>&nbsp;&nbsp;&nbsp;&nbsp;roles?: string[];                       <span className="docs-token-comment">{/* Named roles for a team */}</span></div>
              <div>&nbsp;&nbsp;&nbsp;&nbsp;availability?: &quot;on_demand&quot; | &quot;part_time&quot; | &quot;full_time&quot; | &quot;24_7&quot;;</div>
              <div>&nbsp;&nbsp;&nbsp;&nbsp;mode?: &quot;single&quot; | &quot;persistent&quot; | &quot;team&quot; | &quot;auto&quot;;</div>
              <div>&nbsp;&nbsp;&nbsp;&nbsp;count?: number;</div>
              <div>&nbsp;&nbsp;&nbsp;&nbsp;profile?: string;</div>
              <div>&nbsp;&nbsp;&nbsp;&nbsp;size?: number;</div>
              <div>&nbsp;&nbsp;<span className="docs-token-operator">{`}`}</span>;</div>
              <div>&nbsp;&nbsp;work: string;                              <span className="docs-token-comment">{/* Required */}</span></div>
              <div>&nbsp;&nbsp;resources?: <span className="docs-token-operator">{`{`}</span></div>
              <div>&nbsp;&nbsp;&nbsp;&nbsp;credentials?: string; vaultCredentials?: string[];</div>
              <div>&nbsp;&nbsp;&nbsp;&nbsp;credentialConnections?: string[];</div>
              <div>&nbsp;&nbsp;&nbsp;&nbsp;agentIdentity?: string;</div>
              <div>&nbsp;&nbsp;&nbsp;&nbsp;card?: string;</div>
              <div>&nbsp;&nbsp;&nbsp;&nbsp;budget?: number;</div>
              <div>&nbsp;&nbsp;&nbsp;&nbsp;files?: string[];</div>
              <div>&nbsp;&nbsp;&nbsp;&nbsp;tools?: string[];</div>
              <div>&nbsp;&nbsp;&nbsp;&nbsp;scopes?: string[];</div>
              <div>&nbsp;&nbsp;&nbsp;&nbsp;mcp?: Record&lt;string, string&gt;;</div>
              <div>&nbsp;&nbsp;&nbsp;&nbsp;customTools?: CustomTool[];</div>
              <div>&nbsp;&nbsp;<span className="docs-token-operator">{`}`}</span>;</div>
              <div>&nbsp;&nbsp;guardrails?: <span className="docs-token-operator">{`{`}</span> approvals?: ApprovalCategory[]; preauthorizeConsequential?: boolean; timeout?: number <span className="docs-token-operator">{`}`}</span>;</div>
              <div>&nbsp;&nbsp;outcome?: string | Object;</div>
              <div>&nbsp;&nbsp;output?: <span className="docs-token-operator">{`{`}</span> captureSource?: &quot;cloud&quot; | &quot;client&quot;; recording?: boolean; piiMasking?: boolean; contract?: OutputContract <span className="docs-token-operator">{`}`}</span>;</div>
              <div>&nbsp;&nbsp;idempotencyKey?: string;                   <span className="docs-token-comment">{/* de-dupes retries — see Idempotency */}</span></div>
              <div>&nbsp;&nbsp;errorHandling?: <span className="docs-token-operator">{`{`}</span> retryPolicy?: &quot;exponential_backoff&quot; | &quot;fixed&quot; | &quot;none&quot;; maxRetries?: number; fallbackStrategy?: string; partialFailureHandling?: string <span className="docs-token-operator">{`}`}</span>;</div>
              <div>&nbsp;&nbsp;dryRun?: boolean;                           <span className="docs-token-comment">{/* plan only, no execution */}</span></div>
              <div>&nbsp;&nbsp;validateOnly?: boolean;                     <span className="docs-token-comment">{/* validate config, no run */}</span></div>
              <div>&nbsp;&nbsp;returnPlan?: boolean;</div>
              <div>&nbsp;&nbsp;estimateCosts?: boolean;</div>
              <div>&nbsp;&nbsp;scheduleAt?: string;                         <span className="docs-token-comment">{/* ISO date-time, up to 365 days ahead */}</span></div>
              <div>&nbsp;&nbsp;onUpdate?: (event: TraceEvent) =&gt; void;</div>
              <div>&nbsp;&nbsp;onApproval?: (req: ApprovalRequest) =&gt; Promise&lt;<span className="docs-token-operator">{`{`}</span> approved: boolean; modifiedContext?: unknown <span className="docs-token-operator">{`}`}</span>&gt;;</div>
              <div>&nbsp;&nbsp;webhook?: string;</div>
              <div>&nbsp;&nbsp;metadata?: Record&lt;string, string&gt;;</div>
              <div><span className="docs-token-operator">{`}`}</span></div>
            </div>
          </div>

          <h4 className="docs-subtitle">Returns</h4>
          <div className="docs-code-block">
            <div className="docs-code-header">
              <span className="docs-lang">TYPESCRIPT</span>
            </div>
            <div className="docs-code-content">
              <div><span className="docs-token-keyword">interface</span> <span className="docs-token-class">WorkerRun</span> <span className="docs-token-operator">{`{`}</span></div>
              <div>&nbsp;&nbsp;id: string;</div>
              <div>&nbsp;&nbsp;status: &quot;pending&quot; | &quot;running&quot; | &quot;needs_approval&quot; | &quot;stuck&quot; | &quot;verifying&quot; | &quot;completed&quot; | &quot;failed&quot; | &quot;canceled&quot;;</div>
              <div>&nbsp;&nbsp;work?: string;</div>
              <div>&nbsp;&nbsp;createdAt?: string; updatedAt?: string; startedAt?: string; finishedAt?: string; scheduledAt?: string;</div>
              <div>&nbsp;&nbsp;outcome?: unknown;</div>
              <div>&nbsp;&nbsp;outputCheckpoint?: OutputCheckpoint;</div>
              <div>&nbsp;&nbsp;captureSource?: &quot;cloud&quot; | &quot;client&quot;;</div>
              <div>&nbsp;&nbsp;cost?: RunCost;</div>
              <div>&nbsp;&nbsp;verification?: <span className="docs-token-operator">{`{`}</span> status: &quot;passed&quot; | &quot;failed&quot; | &quot;unverified&quot;; method: &quot;self&quot; | &quot;independent&quot;; checks: VerificationCheck[] <span className="docs-token-operator">{`}`}</span>;</div>
              <div>&nbsp;&nbsp;trace?: TraceEvent[];  <span className="docs-token-comment">{/* includes screenshot proof events */}</span></div>
              <div>&nbsp;&nbsp;recording?: <span className="docs-token-operator">{`{`}</span> url: string; duration_ms: number <span className="docs-token-operator">{`}`}</span>;  <span className="docs-token-comment">{/* present only when output.recording */}</span></div>
              <div>&nbsp;&nbsp;recordings?: Recording[];                    <span className="docs-token-comment">{/* additive team-mode extension */}</span></div>
              <div>&nbsp;&nbsp;error?: <span className="docs-token-operator">{`{`}</span> code: ErrorCode; message: string; blockedReason?: string <span className="docs-token-operator">{`}`}</span>;  <span className="docs-token-comment">{/* on failed / stuck */}</span></div>
              <div>&nbsp;&nbsp;plan?: unknown; costEstimate?: <span className="docs-token-operator">{`{`}</span> total_usd: number <span className="docs-token-operator">{`}`}</span>;  <span className="docs-token-comment">{/* dryRun only */}</span></div>
              <div>&nbsp;&nbsp;metadata?: Record&lt;string, string&gt;;</div>
              <div>&nbsp;&nbsp;webhookSigningSecret?: string;               <span className="docs-token-comment">{/* submission response only */}</span></div>
              <div><span className="docs-token-operator">{`}`}</span></div>
            </div>
          </div>

          <h3 className="docs-subtitle">workers.create(config)</h3>
          <p className="docs-text">
            Create a persistent worker that can receive repeated assignments.
            Use this for BPO operations, recurring admin work, and long-lived
            worker identities. Calling <code>workers.run</code> with{" "}
            <code>worker.mode: &quot;persistent&quot;</code> is shorthand that auto-creates a worker
            and assigns the run to it; use <code>create()</code> + <code>assign()</code> when you
            need to reuse one worker identity across many assignments.
          </p>

          <h4 className="docs-subtitle">Persistent Worker Parameters</h4>
          <div className="docs-code-block">
            <div className="docs-code-header">
              <span className="docs-lang">TYPESCRIPT</span>
            </div>
            <div className="docs-code-content">
              <div><span className="docs-token-keyword">interface</span> <span className="docs-token-class">WorkerCreateConfig</span> <span className="docs-token-operator">{`{`}</span></div>
              <div>&nbsp;&nbsp;name: string;</div>
              <div>&nbsp;&nbsp;role?: string; roles?: string[];</div>
              <div>&nbsp;&nbsp;mode?: &quot;persistent&quot;;</div>
              <div>&nbsp;&nbsp;availability?: &quot;part_time&quot; | &quot;full_time&quot; | &quot;custom&quot; | &quot;24_7&quot;;</div>
              <div>&nbsp;&nbsp;availabilitySchedule?: WorkerAvailabilitySchedule;</div>
              <div>&nbsp;&nbsp;instructions?: string; skills?: WorkerSkill[];</div>
              <div>&nbsp;&nbsp;count?: number; size?: number;</div>
              <div>&nbsp;&nbsp;profile?: string;</div>
              <div>&nbsp;&nbsp;resources?: Resources;</div>
              <div>&nbsp;&nbsp;memory?: <span className="docs-token-operator">{`{`}</span> enabled?: boolean <span className="docs-token-operator">{`}`}</span>;</div>
              <div>&nbsp;&nbsp;guardrails?: Guardrails;</div>
              <div>&nbsp;&nbsp;metadata?: Record&lt;string, string&gt;;</div>
              <div><span className="docs-token-operator">{`}`}</span></div>
            </div>
          </div>

          <h4 className="docs-subtitle">Returns</h4>
          <div className="docs-code-block">
            <div className="docs-code-header">
              <span className="docs-lang">TYPESCRIPT</span>
            </div>
            <div className="docs-code-content">
              <div><span className="docs-token-keyword">interface</span> <span className="docs-token-class">Worker</span> <span className="docs-token-operator">{`{`}</span></div>
              <div>&nbsp;&nbsp;id: string;</div>
              <div>&nbsp;&nbsp;name: string;</div>
              <div>&nbsp;&nbsp;status: &quot;ready&quot; | &quot;running&quot; | &quot;paused&quot;;</div>
              <div>&nbsp;&nbsp;assign: (config: WorkerAssignment) =&gt; Promise&lt;WorkerRun&gt;;</div>
              <div><span className="docs-token-operator">{`}`}</span></div>
            </div>
          </div>
        </section>

        <section id="types" className="docs-section">
          <h2 className="docs-section-title">Type Definitions</h2>
          <p className="docs-text">
            Selected convenience types used by the private-beta SDK examples.
            The generated OpenAPI schemas are canonical and include complete
            validation constraints.
          </p>
          <div className="docs-code-block">
            <div className="docs-code-header"><span className="docs-lang">TYPESCRIPT</span></div>
            <div className="docs-code-content">
              <div><span className="docs-token-keyword">type</span> <span className="docs-token-class">ApprovalCategory</span> = &quot;messages&quot; | &quot;external_submit&quot; | &quot;payments&quot; | &quot;trades&quot;;</div>
              <div><span className="docs-token-keyword">type</span> <span className="docs-token-class">WebhookEventType</span> = &quot;worker.started&quot; | &quot;worker.needs_approval&quot; | &quot;worker.verifying&quot; | &quot;worker.stuck&quot; | &quot;worker.completed&quot; | &quot;worker.failed&quot;;</div>
              <div><span className="docs-token-keyword">type</span> <span className="docs-token-class">ErrorCode</span> = string; <span className="docs-token-comment">{/* Examples include invalid_request, authentication_error, and rate_limited. Handle unknown codes. */}</span></div>
              <br />
              <div><span className="docs-token-keyword">interface</span> <span className="docs-token-class">TraceEvent</span> <span className="docs-token-operator">{`{`}</span></div>
              <div>&nbsp;&nbsp;type: &quot;worker_thinking&quot; | &quot;browser_runtime_recovery&quot; | &quot;browser_runtime_recovered&quot; |</div>
              <div>&nbsp;&nbsp;&nbsp;&nbsp;&quot;decision_started&quot; | &quot;decision_ready&quot; | &quot;action_review_started&quot; | &quot;action_review_ready&quot; |</div>
              <div>&nbsp;&nbsp;&nbsp;&nbsp;&quot;freshness_check_started&quot; | &quot;freshness_check_ready&quot; | &quot;continuity_check_started&quot; |</div>
              <div>&nbsp;&nbsp;&nbsp;&nbsp;&quot;continuity_check_ready&quot; | &quot;action_committed&quot; | &quot;action_started&quot; | &quot;action_withheld&quot; |</div>
              <div>&nbsp;&nbsp;&nbsp;&nbsp;&quot;observation_started&quot; | &quot;observation_ready&quot; | &quot;tool_used&quot; | &quot;screenshot&quot; |</div>
              <div>&nbsp;&nbsp;&nbsp;&nbsp;&quot;demonstrated&quot; | &quot;watching&quot; | &quot;human_acted&quot; | &quot;coached&quot; |</div>
              <div>&nbsp;&nbsp;&nbsp;&nbsp;&quot;proof_artifact_ready&quot; | &quot;proof_artifact_failed&quot; | &quot;output_progress&quot;;</div>
              <div>&nbsp;&nbsp;ts: string;</div>
              <div>&nbsp;&nbsp;workerId?: string;             <span className="docs-token-comment">{/* which worker in a team */}</span></div>
              <div>&nbsp;&nbsp;screenshotUrl?: string;</div>
              <div>&nbsp;&nbsp;payload?: unknown;</div>
              <div><span className="docs-token-operator">{`}`}</span></div>
              <br />
              <div><span className="docs-token-keyword">interface</span> <span className="docs-token-class">VerificationCheck</span> <span className="docs-token-operator">{`{`}</span> name: string; passed: boolean; evidence?: string <span className="docs-token-operator">{`}`}</span></div>
              <br />
              <div><span className="docs-token-keyword">interface</span> <span className="docs-token-class">ApprovalRequest</span> <span className="docs-token-operator">{`{`}</span> runId: string; category: ApprovalCategory; action: string; details: unknown <span className="docs-token-operator">{`}`}</span></div>
              <br />
              <div><span className="docs-token-keyword">type</span> <span className="docs-token-class">CustomTool</span> = <span className="docs-token-operator">{`{`}</span> name: string; description?: string; inputSchema?: object <span className="docs-token-operator">{`}`}</span> &amp; (<span className="docs-token-operator">{`{`}</span> execution?: &quot;http&quot;; endpoint: string; auth?: string <span className="docs-token-operator">{`}`}</span> | <span className="docs-token-operator">{`{`}</span> execution: &quot;client&quot;; endpoint?: never; auth?: never <span className="docs-token-operator">{`}`}</span>);</div>
              <div><span className="docs-token-keyword">interface</span> <span className="docs-token-class">WorkerSkill</span> <span className="docs-token-operator">{`{`}</span> name: string; instructions: string <span className="docs-token-operator">{`}`}</span></div>
              <br />
              <div><span className="docs-token-keyword">interface</span> <span className="docs-token-class">WorkerAssignment</span> <span className="docs-token-operator">{`{`}</span> work: string; instructions?: string; skills?: WorkerSkill[]; retainContext?: boolean; scheduleAt?: string; outcome?: string | Object; resources?: Resources; guardrails?: Guardrails; output?: object; idempotencyKey?: string; errorHandling?: ErrorHandling; webhook?: string; metadata?: Record&lt;string, string&gt; <span className="docs-token-operator">{`}`}</span></div>
              <br />
              <div><span className="docs-token-keyword">interface</span> <span className="docs-token-class">WebhookPayload</span> <span className="docs-token-operator">{`{`}</span> type: WebhookEventType; run_id: string; event_id: string; timestamp: string; data?: object; outcome?: unknown; cost?: RunCost; verification?: Verification; trace_url?: string; error?: RunError <span className="docs-token-operator">{`}`}</span></div>
              <br />
              <div><span className="docs-token-keyword">interface</span> <span className="docs-token-class">OutputContract</span> <span className="docs-token-operator">{`{`}</span> version: &quot;output_v1&quot;; description: string; deliverables?: OutputDeliverable[] <span className="docs-token-operator">{`}`}</span></div>
            </div>
          </div>

          <h3 className="docs-subtitle">Run &amp; worker methods</h3>
          <ul className="docs-list">
            <li><code>client.workers.retrieveRun(runId)</code> - fetch one run by id</li>
            <li><code>client.workers.listRuns()</code> - paginated list of runs (<code>limit</code>, <code>cursor</code> params)</li>
            <li><code>client.workers.cancel(runId)</code> - stop an in-flight run</li>
            <li><code>client.workers.pause(workerId)</code> / <code>.resume(workerId)</code> / <code>.delete(workerId)</code> - manage a persistent worker (this is how a worker reaches the <code>&quot;paused&quot;</code> status)</li>
          </ul>
        </section>

        <section id="verification" className="docs-section">
          <h2 className="docs-section-title">Verification &amp; Proof</h2>
          <p className="docs-text">
            Every run returns evidence, not just an answer. Alongside{" "}
            <code>outcome</code>, a completed run returns <code>cost</code> (actual
            spend), a <code>trace</code> with screenshot proof of each step, and an
            independent <code>verification</code> result checked against the{" "}
            <code>outcome</code> criteria you set.
          </p>
          <ul className="docs-list">
            <li><strong>verification.status: &quot;passed&quot;</strong> - an independent check confirmed the outcome against your criteria.</li>
            <li><strong>&quot;failed&quot;</strong> - the check found the outcome does not meet the criteria. The run is flagged, never silently returned as done.</li>
            <li><strong>&quot;unverified&quot;</strong> - the outcome could not be confirmed yet (e.g. a source is pending). The run stays in <code>verifying</code> and re-checks on a schedule until it can close or escalate.</li>
          </ul>
          <p className="docs-text">
            Pass <code>outcome</code> as an object to set structured, checkable
            criteria. The screenshot trace and verification checks are retained as
            an audit trail for regulated workflows. Screenshots are the default
            proof; set <code>output.recording = true</code> to also get a full
            video replay when a person needs to watch the run back &mdash; a
            reviewer verifying, or a learner rewatching a session.
          </p>
          <p className="docs-text">
            Use <code>output.contract</code> when the worker must produce validated
            JSON, CSV, or XLSX deliverables. Set <code>output.piiMasking = true</code>{" "}
            to de-identify screenshot proof and trace payloads before persistence.
            Recordings are not masked and can have independent retention.
          </p>
          <div className="docs-code-block">
            <div className="docs-code-header">
              <span className="docs-lang">JSON</span>
            </div>
            <div className="docs-code-content">
              <div>&quot;output&quot;: <span className="docs-token-operator">{`{`}</span></div>
              <div>&nbsp;&nbsp;&quot;recording&quot;: <span className="docs-token-boolean">true</span>,</div>
              <div>&nbsp;&nbsp;&quot;piiMasking&quot;: <span className="docs-token-boolean">true</span>,</div>
              <div>&nbsp;&nbsp;&quot;contract&quot;: <span className="docs-token-operator">{`{`}</span></div>
              <div>&nbsp;&nbsp;&nbsp;&nbsp;&quot;version&quot;: <span className="docs-token-string">&quot;output_v1&quot;</span>,</div>
              <div>&nbsp;&nbsp;&nbsp;&nbsp;&quot;description&quot;: <span className="docs-token-string">&quot;Return the completed reconciliation as XLSX&quot;</span>,</div>
              <div>&nbsp;&nbsp;&nbsp;&nbsp;&quot;deliverables&quot;: [<span className="docs-token-operator">{`{`}</span> &quot;id&quot;: &quot;report&quot;, &quot;filename&quot;: &quot;reconciliation.xlsx&quot;, &quot;format&quot;: &quot;xlsx&quot;, &quot;shape&quot;: &quot;array&quot;, &quot;record_schema&quot;: <span className="docs-token-operator">{`{`}</span> &quot;type&quot;: &quot;object&quot; <span className="docs-token-operator">{`}`}</span> <span className="docs-token-operator">{`}`}</span>]</div>
              <div>&nbsp;&nbsp;<span className="docs-token-operator">{`}`}</span></div>
              <div><span className="docs-token-operator">{`}`}</span></div>
            </div>
          </div>
          <p className="docs-text">
            Use the run and per-agent artifact endpoints to list and download
            immutable screenshots, recordings, and output files. Each artifact
            includes its MIME type, size, SHA-256 digest, creation time, and
            retention deadline.
          </p>
        </section>

        <section id="collaboration" className="docs-section">
          <h2 className="docs-section-title">Collaboration — Pair &amp; Teach</h2>
          <p className="docs-text">
            A worker is not limited to solo autonomous work. Like a person, it can
            share an environment with a human — watch what they do, demonstrate a
            step, let them try, and coach as they go. These are <strong>innate
            abilities, not a separate mode</strong>: the worker decides <em>when</em>
            to work alone, pair, or teach from the work and role you give it, exactly
            as a colleague would.
          </p>
          <ul className="docs-list">
            <li><strong>Watch</strong> - perceives the human&apos;s live actions on the shared screen, not just the page.</li>
            <li><strong>Demonstrate</strong> - performs a step to show how, then holds.</li>
            <li><strong>Observe &amp; stay present</strong> - keeps watching while the human attempts it. This is continuous co-presence, never a blind pause.</li>
            <li><strong>Coach</strong> - gives feedback or corrects, without seizing control.</li>
          </ul>
          <p className="docs-text">
            You never set a &quot;teaching mode.&quot; Assign work like{" "}
            <code>&quot;Train the new analyst to run month-end reconciliation&quot;</code> or{" "}
            <code>&quot;Pair with me on this filing&quot;</code>, and the worker chooses
            co-presence on its own. The trace records <code>demonstrated</code>,{" "}
            <code>watching</code>, <code>human_acted</code>, and <code>coached</code>{" "}
            events, so the session stays fully auditable and replayable.
          </p>
          <ul className="docs-list">
            <li><code>GET /v1/runs/{"{runId}"}/live-preview</code> - stream a read-only worker desktop.</li>
            <li><code>POST /v1/runs/{"{runId}"}/collaboration</code> - create a single-use, short-lived shared-screen link.</li>
            <li><code>GET /v1/runs/{"{runId}"}/agents</code> - list addressable workers in a team run.</li>
            <li><code>GET /v1/runs/{"{runId}"}/agents/{"{agentId}"}/live-preview</code> - preview one worker.</li>
            <li><code>POST /v1/runs/{"{runId}"}/agents/{"{agentId}"}/collaboration</code> - collaborate with one worker.</li>
          </ul>
        </section>

        <section id="memory" className="docs-section">
          <h2 className="docs-section-title">Memory &amp; Learning</h2>
          <p className="docs-text">
            Set <code>memory.enabled</code> on a worker to let it remember across
            assignments. Memory works at three levels, like a person&apos;s:
          </p>
          <ul className="docs-list">
            <li><strong>Working memory</strong> - within a single run, the worker carries context across every step; the <code>trace</code> is its record.</li>
            <li><strong>Long-term memory</strong> - a persistent worker retains what it learned across assignments (how a portal is laid out, where a control lives, what failed last time) to inform later assignments. Memory does not guarantee accuracy or faster execution.</li>
            <li><strong>Learning from coaching</strong> - in a Pair &amp; Teach session, when a human corrects or demonstrates, that feedback is retained and applied to later work. The worker improves from being taught, not only from doing.</li>
          </ul>
          <p className="docs-text">
            Memory is per-worker and tenant-isolated. You can inspect and clear it, and
            nothing persists unless you enable it — off by default. Use{" "}
            <code>GET /v1/workers/{"{workerId}"}/memory</code> to inspect the
            current version and{" "}
            <code>DELETE /v1/workers/{"{workerId}"}/memory</code> to clear it
            immediately.
          </p>
        </section>

        <section id="production" className="docs-section">
          <h2 className="docs-section-title">Production Essentials</h2>
          <p className="docs-text">
            Everything you need before pointing real, money-moving, form-submitting
            traffic at a worker.
          </p>

          <h3 className="docs-subtitle">Idempotency</h3>
          <p className="docs-text">
            Send an 8-200 character <code>Idempotency-Key</code> header when creating
            runs or financial operations. The SDK&apos;s <code>idempotencyKey</code>{" "}
            field maps to that header. An exact retry returns the original operation;
            reusing a key with different inputs returns <code>409 Conflict</code>.
          </p>

          <h3 className="docs-subtitle">Errors &amp; Status Codes</h3>
          <p className="docs-text">
            Errors return a non-2xx HTTP status and a typed body with an <code>error</code>
            object carrying <code>code</code> (an <code>ErrorCode</code>), <code>message</code>,
            and <code>requestId</code>.
          </p>
          <ul className="docs-list">
            <li><code>400</code> invalid request &middot; <code>401</code> authentication failure &middot; <code>402</code> budget or billing failure &middot; <code>403</code> forbidden &middot; <code>404</code> not found &middot; <code>409</code> conflicting state or mismatched idempotency reuse &middot; <code>422</code> invalid configuration &middot; <code>429</code> rate limited &middot; <code>503</code> temporarily unavailable</li>
          </ul>

          <h3 className="docs-subtitle">Rate Limits</h3>
          <p className="docs-text">
            Over the limit returns <code>429</code> with a <code>Retry-After</code> header and
            <code> X-RateLimit-Remaining</code> / <code>X-RateLimit-Reset</code>. Back off and
            retry; the SDK does this automatically with jitter. Per-plan quotas are on your
            dashboard.
          </p>

          <h3 className="docs-subtitle">Cancellation</h3>
          <p className="docs-text">
            <code>POST /v1/runs/{"{runId}"}/cancel</code> durably requests
            cancellation and returns <code>202</code>; an already terminal run
            returns <code>200</code>. A canceled run settles to{" "}
            <code>status = &quot;canceled&quot;</code>. Persistent workers use{" "}
            <code>pause</code>, <code>resume</code>, and <code>delete</code>.
          </p>

          <h3 className="docs-subtitle">Environments &amp; Versioning</h3>
          <p className="docs-text">
            The API is versioned in the base path. The Swagger reference currently
            targets <code>https://api.staging.grupa.ai/v1</code>; use the production
            base URL supplied with production access. Breaking changes ship under a
            new version. Keys prefixed <code>grupa_test_</code> run
            in a <strong>sandbox</strong> that simulates external submits and payments — no real
            money moves and no real forms are filed — so you can integrate safely. Override the
            host by passing a <code>baseURL</code> option to the client.
          </p>

          <h3 className="docs-subtitle">Webhook Signing</h3>
          <p className="docs-text">
            Every webhook POST includes an <code>X-Grupa-Signature</code> header: an HMAC-SHA256
            of the raw request body using your webhook secret. Recompute it and compare in
            constant time before trusting the payload; reject anything that doesn&apos;t match or
            is unsigned. This is what prevents a spoofed <code>worker.completed</code> or a forged
            payment approval.
          </p>
        </section>

        <section id="testing" className="docs-section">
          <h2 className="docs-section-title">Testing & Development</h2>
          <p className="docs-text">
            Test worker instructions, resources, guardrails, and expected outcomes before running in production.
          </p>

          <h3 className="docs-subtitle">Dry Run Mode</h3>
          <p className="docs-text">
            Plan and validate worker steps without executing actual external actions.
          </p>
          <div className="docs-code-block">
            <div className="docs-code-header">
              <span className="docs-lang">JAVASCRIPT</span>
            </div>
            <div className="docs-code-content">
              <div><span className="docs-token-keyword">const</span> plan <span className="docs-token-operator">=</span> <span className="docs-token-keyword">await</span> client.workers.<span className="docs-token-function">run</span>(<span className="docs-token-operator">{`{`}</span></div>
              <div>&nbsp;&nbsp;work: <span className="docs-token-string">&quot;Research top 5 autonomous worker platforms&quot;</span>,</div>
              <div>&nbsp;&nbsp;dryRun: <span className="docs-token-boolean">true</span>, <span className="docs-token-comment">{/* No execution, plan only */}</span></div>
              <div>&nbsp;&nbsp;returnPlan: <span className="docs-token-boolean">true</span>, <span className="docs-token-comment">{/* Get execution strategy */}</span></div>
              <div>&nbsp;&nbsp;estimateCosts: <span className="docs-token-boolean">true</span> <span className="docs-token-comment">{/* Get cost estimate */}</span></div>
              <div><span className="docs-token-operator">{`}`}</span>);</div>
              <br />
              <div>console.<span className="docs-token-function">log</span>(<span className="docs-token-string">&quot;Execution plan:&quot;</span>, plan.plan);</div>
              <div>console.<span className="docs-token-function">log</span>(<span className="docs-token-string">&quot;Estimated cost:&quot;</span>, plan.costEstimate);</div>
            </div>
          </div>

          <h3 className="docs-subtitle">Validation Mode</h3>
          <p className="docs-text">
            Validate worker configuration and connected tools without running the full workflow.
          </p>
          <div className="docs-code-block">
            <div className="docs-code-header">
              <span className="docs-lang">JAVASCRIPT</span>
            </div>
            <div className="docs-code-content">
              <div><span className="docs-token-keyword">const</span> result <span className="docs-token-operator">=</span> <span className="docs-token-keyword">await</span> client.workers.<span className="docs-token-function">run</span>(<span className="docs-token-operator">{`{`}</span></div>
              <div>&nbsp;&nbsp;work: <span className="docs-token-string">&quot;Analyze Q3 sales data&quot;</span>,</div>
              <div>&nbsp;&nbsp;validateOnly: <span className="docs-token-boolean">true</span>, <span className="docs-token-comment">{/* Validate configuration */}</span></div>
              <div>&nbsp;&nbsp;resources: <span className="docs-token-operator">{`{`}</span></div>
              <div>&nbsp;&nbsp;&nbsp;&nbsp;mcp: <span className="docs-token-operator">{`{`}</span> postgres: <span className="docs-token-string">&quot;postgresql://...&quot;</span> <span className="docs-token-operator">{`}`}</span></div>
              <div>&nbsp;&nbsp;<span className="docs-token-operator">{`}`}</span></div>
              <div><span className="docs-token-operator">{`}`}</span>);</div>
              <br />
              <div>console.<span className="docs-token-function">log</span>(<span className="docs-token-string">&quot;Validation status:&quot;</span>, result.status);</div>
              <div><span className="docs-token-keyword">if</span> (result.error) <span className="docs-token-operator">{`{`}</span></div>
              <div>&nbsp;&nbsp;console.<span className="docs-token-function">error</span>(result.error.code, result.error.message);</div>
              <div><span className="docs-token-operator">{`}`}</span></div>
            </div>
          </div>
        </section>

        <section id="error-handling" className="docs-section">
          <h2 className="docs-section-title">Error Handling & Recovery</h2>
          <p className="docs-text">
            Build robust worker runs with comprehensive error handling and recovery strategies.
          </p>

          <h3 className="docs-subtitle">Mid-flight Intervention</h3>
          <p className="docs-text">
            Pause and intervene when workers need human guidance, approval, or updated instructions.
          </p>
          <div className="docs-code-block">
            <div className="docs-code-header">
              <span className="docs-lang">JAVASCRIPT</span>
            </div>
            <div className="docs-code-content">
              <div><span className="docs-token-keyword">const</span> run <span className="docs-token-operator">=</span> <span className="docs-token-keyword">await</span> client.workers.<span className="docs-token-function">run</span>(<span className="docs-token-operator">{`{`}</span></div>
              <div>&nbsp;&nbsp;work: <span className="docs-token-string">&quot;Negotiate enterprise partnership terms in vendor portal&quot;</span>,</div>
              <div>&nbsp;&nbsp;guardrails: <span className="docs-token-operator">{`{`}</span> approvals: [<span className="docs-token-string">&quot;external_submit&quot;</span>] <span className="docs-token-operator">{`}`}</span>,</div>
              <div>&nbsp;&nbsp;onApproval: <span className="docs-token-keyword">async</span> (request) <span className="docs-token-operator">=&gt;</span> <span className="docs-token-operator">{`{`}</span></div>
              <div>&nbsp;&nbsp;&nbsp;&nbsp;<span className="docs-token-comment">{/* Pause workflow and show approval UI */}</span></div>
              <div>&nbsp;&nbsp;&nbsp;&nbsp;<span className="docs-token-keyword">const</span> decision <span className="docs-token-operator">=</span> <span className="docs-token-keyword">await</span> <span className="docs-token-function">showApprovalUI</span>(request);</div>
              <div>&nbsp;&nbsp;&nbsp;&nbsp;</div>
              <div>&nbsp;&nbsp;&nbsp;&nbsp;<span className="docs-token-keyword">if</span> (decision.action <span className="docs-token-operator">===</span> <span className="docs-token-string">&quot;modify_strategy&quot;</span>) <span className="docs-token-operator">{`{`}</span></div>
              <div>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;<span className="docs-token-comment">{/* Update strategy and continue */}</span></div>
              <div>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;<span className="docs-token-keyword">return</span> <span className="docs-token-operator">{`{`}</span></div>
              <div>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;approved: <span className="docs-token-boolean">true</span>,</div>
              <div>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;modifiedContext: decision.newStrategy</div>
              <div>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;<span className="docs-token-operator">{`}`}</span>;</div>
              <div>&nbsp;&nbsp;&nbsp;&nbsp;<span className="docs-token-operator">{`}`}</span></div>
              <div>&nbsp;&nbsp;&nbsp;&nbsp;</div>
              <div>&nbsp;&nbsp;&nbsp;&nbsp;<span className="docs-token-keyword">return</span> <span className="docs-token-operator">{`{`}</span> approved: decision.approved <span className="docs-token-operator">{`}`}</span>;</div>
              <div>&nbsp;&nbsp;<span className="docs-token-operator">{`}`}</span>,</div>
              <div>&nbsp;&nbsp;resources: <span className="docs-token-operator">{`{`}</span> budget: <span className="docs-token-number">500</span> <span className="docs-token-operator">{`}`}</span></div>
              <div><span className="docs-token-operator">{`}`}</span>);</div>
            </div>
          </div>

          <h3 className="docs-subtitle">Error Recovery Strategies</h3>
          <p className="docs-text">
            Configure how workers handle failures and retry logic.
          </p>
          <div className="docs-code-block">
            <div className="docs-code-header">
              <span className="docs-lang">JAVASCRIPT</span>
            </div>
            <div className="docs-code-content">
              <div><span className="docs-token-keyword">const</span> run <span className="docs-token-operator">=</span> <span className="docs-token-keyword">await</span> client.workers.<span className="docs-token-function">run</span>(<span className="docs-token-operator">{`{`}</span></div>
              <div>&nbsp;&nbsp;work: <span className="docs-token-string">&quot;Process customer data and generate insights&quot;</span>,</div>
              <div>&nbsp;&nbsp;errorHandling: <span className="docs-token-operator">{`{`}</span></div>
              <div>&nbsp;&nbsp;&nbsp;&nbsp;retryPolicy: <span className="docs-token-string">&quot;exponential_backoff&quot;</span>,</div>
              <div>&nbsp;&nbsp;&nbsp;&nbsp;maxRetries: <span className="docs-token-number">3</span>,</div>
              <div>&nbsp;&nbsp;&nbsp;&nbsp;fallbackStrategy: <span className="docs-token-string">&quot;use_alternative_tools&quot;</span>,</div>
              <div>&nbsp;&nbsp;&nbsp;&nbsp;partialFailureHandling: <span className="docs-token-string">&quot;continue_with_available_data&quot;</span></div>
              <div>&nbsp;&nbsp;<span className="docs-token-operator">{`}`}</span>,</div>
              <div>&nbsp;&nbsp;resources: <span className="docs-token-operator">{`{`}</span> budget: <span className="docs-token-number">100</span> <span className="docs-token-operator">{`}`}</span></div>
              <div><span className="docs-token-operator">{`}`}</span>);</div>
            </div>
          </div>
        </section>

        <section id="security" className="docs-section">
          <h2 className="docs-section-title">Security & Compliance</h2>
          <p className="docs-text">
            Enterprise-grade security with credential management and audit trails.
          </p>

          <h3 className="docs-subtitle">Credential Vault</h3>
          <p className="docs-text">
            Portal logins and payment methods live in the credentials vault. Reference
            vault IDs in worker runs — never embed raw secrets in source code. Secrets are
            encrypted at rest with per-tenant keys and injected only at the browser/execution
            layer at the moment of use: the reasoning model never receives raw credential
            values, and they are redacted from traces, screenshots, and logs.
          </p>
          <div className="docs-code-block">
            <div className="docs-code-header">
              <span className="docs-lang">JAVASCRIPT</span>
            </div>
            <div className="docs-code-content">
              <div><span className="docs-token-keyword">await</span> client.workers.<span className="docs-token-function">run</span>(<span className="docs-token-operator">{`{`}</span></div>
              <div>&nbsp;&nbsp;work: <span className="docs-token-string">&quot;Update records in CRM portal&quot;</span>,</div>
              <div>&nbsp;&nbsp;resources: <span className="docs-token-operator">{`{`}</span> credentials: <span className="docs-token-string">&quot;vault_crm_prod&quot;</span> <span className="docs-token-operator">{`}`}</span></div>
              <div><span className="docs-token-operator">{`}`}</span>);</div>
            </div>
          </div>

          <h3 className="docs-subtitle">Data Handling &amp; Retention</h3>
          <p className="docs-text">
            Traces, screenshots, and recordings can contain PII/PHI by construction — a portal
            screen holds real data. Artifacts are encrypted at rest, access-controlled per
            tenant, and returned with an explicit <code>retentionUntil</code>.{" "}
            <code>DELETE /v1/data-retention/runs/{"{runId}"}</code> cancels a live
            run and permanently deletes its parent/child data. Set{" "}
            <code>output.piiMasking = true</code> to de-identify screenshot proof
            and trace payloads before persistence. Recordings remain unmasked and may have
            independent retention even when masking is enabled. Keep <code>work</code> and{" "}
            <code>outcome</code> free of unnecessary PII — treat them as logged.
          </p>

          <h3 className="docs-subtitle">Deployment &amp; Isolation</h3>
          <p className="docs-text">
            Workers run in isolated, per-run sandboxes with tenant data separation; browser
            state, cookies, and cache are wiped between assignments. Single-tenant and
            customer-VPC deployment (with egress allowlisting) are available for regulated
            buyers. Approvals are enforced and recorded server-side for non-repudiation — not
            trusted from the client callback alone.
          </p>

          <h3 className="docs-subtitle">Access &amp; Keys</h3>
          <ul className="docs-list">
            <li><strong>Scoped keys:</strong> create keys with <code>runs:read</code>, <code>runs:write</code>, <code>keys:manage</code>, <code>audit:read</code>, <code>vaults:use</code>, <code>vaults:manage</code>, or <code>admin</code>.</li>
            <li><strong>Sandbox:</strong> creating a key with <code>sandbox: true</code> returns a <code>grupa_test_</code> key that suppresses consequential external actions.</li>
            <li><strong>Rotation:</strong> use <code>POST /v1/api-keys/{"{keyId}"}/rotate</code>; store the returned secret securely. Creation also returns the original secret on an exact idempotent replay within 24 hours; list APIs never return it.</li>
            <li><strong>Audit:</strong> <code>GET /v1/audit-events</code> exports immutable credential, approval, memory, and key events.</li>
          </ul>

          <h3 className="docs-subtitle">Compliance</h3>
          <p className="docs-text">
            SOC 2 and a DPA (with subprocessor list and data-residency options) are available
            under NDA; a BAA is available for HIPAA-covered workflows. Because workers operate
            consumer-data portals, FCRA roles and permissible-purpose are addressed in your
            agreement. Contact us for current reports and terms — we do not represent
            certifications we do not hold.
          </p>
        </section>

        <section id="examples" className="docs-section">
          <h2 className="docs-section-title">Examples</h2>
          <p className="docs-text">
            Each example is real work companies assign today — job tickets and shift assignments
            you wire into your product, plus function briefs for worker teams.
          </p>

          <h3 className="docs-section-title docs-section-title--group">Replace ops workflows</h3>
          <p className="docs-text">
            Plug these into your app or ops stack. One API call maps to one assignment a coordinator
            would hand to a human today.
          </p>

          <h3 className="docs-subtitle">
            <span className="docs-workflow-badge docs-workflow-badge--ticket">Job ticket</span>
            On-demand portal worker
          </h3>
          <p className="docs-text docs-workflow-human">
            Human parallel: RCM coordinator assigns case #4412 to a portal specialist for prior auth submission.
          </p>
          <div className="docs-code-block">
            <div className="docs-code-header">
              <span className="docs-lang">JAVASCRIPT</span>
            </div>
            <div className="docs-code-content">
              <div><span className="docs-token-keyword">await</span> client.workers.<span className="docs-token-function">run</span>(<span className="docs-token-operator">{`{`}</span></div>
              <div className="docs-code-line docs-ci-1">work: <span className="docs-token-string">&quot;Submit prior auth for patient case #4412 in payer portal&quot;</span>,</div>
              <div className="docs-code-line docs-ci-1">worker: <span className="docs-token-operator">{`{`}</span></div>
              <div className="docs-code-line docs-ci-2">availability: <span className="docs-token-string">&quot;on_demand&quot;</span>,</div>
              <div className="docs-code-line docs-ci-1"><span className="docs-token-operator">{`},`}</span></div>
              <div className="docs-code-line docs-ci-1">resources: <span className="docs-token-operator">{`{`}</span></div>
              <div className="docs-code-line docs-ci-2">credentials: <span className="docs-token-string">&quot;vault_payer_prod&quot;</span>,</div>
              <div className="docs-code-line docs-ci-2">budget: <span className="docs-token-number">75</span>,</div>
              <div className="docs-code-line docs-ci-1"><span className="docs-token-operator">{`}`}</span>,</div>
              <div><span className="docs-token-operator">{`}`}</span>);</div>
            </div>
          </div>

          <h3 className="docs-subtitle">
            <span className="docs-workflow-badge docs-workflow-badge--shift">Shift assignment</span>
            Persistent BPO worker
          </h3>
          <p className="docs-text docs-workflow-human">
            Human parallel: BPO supervisor assigns overnight vendor portal queue clearing for one client account.
          </p>
          <div className="docs-code-block">
            <div className="docs-code-header">
              <span className="docs-lang">JAVASCRIPT</span>
            </div>
            <div className="docs-code-content">
              <div><span className="docs-token-keyword">const</span> worker <span className="docs-token-operator">=</span> <span className="docs-token-keyword">await</span> client.workers.<span className="docs-token-function">create</span>(<span className="docs-token-operator">{`{`}</span></div>
              <div className="docs-code-line docs-ci-1">name: <span className="docs-token-string">&quot;BPO Night Shift&quot;</span>,</div>
              <div className="docs-code-line docs-ci-1">mode: <span className="docs-token-string">&quot;persistent&quot;</span>,</div>
              <div className="docs-code-line docs-ci-1">availability: <span className="docs-token-string">&quot;24_7&quot;</span>,</div>
              <div><span className="docs-token-operator">{`}`}</span>);</div>
              <br />
              <div><span className="docs-token-keyword">await</span> worker.<span className="docs-token-function">assign</span>(<span className="docs-token-operator">{`{`}</span></div>
              <div className="docs-code-line docs-ci-1">work: <span className="docs-token-string">&quot;Clear overnight vendor portal queues for Acme account and escalate exceptions&quot;</span>,</div>
              <div className="docs-code-line docs-ci-1">outcome: <span className="docs-token-string">&quot;Return queue completed, exceptions, and client-ready report&quot;</span>,</div>
              <div><span className="docs-token-operator">{`}`}</span>);</div>
            </div>
          </div>

          <h3 className="docs-subtitle">
            <span className="docs-workflow-badge docs-workflow-badge--shift">Shift assignment</span>
            Enterprise ops worker
          </h3>
          <p className="docs-text docs-workflow-human">
            Human parallel: RevOps lead assigns the Monday standup reconcile across Salesforce, HubSpot, billing, and recruiting.
          </p>
          <div className="docs-code-block">
            <div className="docs-code-header">
              <span className="docs-lang">JAVASCRIPT</span>
            </div>
            <div className="docs-code-content">
              <div><span className="docs-token-keyword">const</span> worker <span className="docs-token-operator">=</span> <span className="docs-token-keyword">await</span> client.workers.<span className="docs-token-function">create</span>(<span className="docs-token-operator">{`{`}</span></div>
              <div className="docs-code-line docs-ci-1">name: <span className="docs-token-string">&quot;Revenue Ops Worker&quot;</span>,</div>
              <div className="docs-code-line docs-ci-1">mode: <span className="docs-token-string">&quot;persistent&quot;</span>,</div>
              <div className="docs-code-line docs-ci-1">resources: <span className="docs-token-operator">{`{`}</span></div>
              <div className="docs-code-line docs-ci-2">credentials: <span className="docs-token-string">&quot;vault_revops_prod&quot;</span>,</div>
              <div className="docs-code-line docs-ci-2">budget: <span className="docs-token-number">250</span>,</div>
              <div className="docs-code-line docs-ci-1"><span className="docs-token-operator">{`},`}</span></div>
              <div className="docs-code-line docs-ci-1">guardrails: <span className="docs-token-operator">{`{`}</span></div>
              <div className="docs-code-line docs-ci-2">approvals: [<span className="docs-token-string">&quot;external_submit&quot;</span>],</div>
              <div className="docs-code-line docs-ci-1"><span className="docs-token-operator">{`}`}</span>,</div>
              <div><span className="docs-token-operator">{`}`}</span>);</div>
              <br />
              <div><span className="docs-token-keyword">await</span> worker.<span className="docs-token-function">assign</span>(<span className="docs-token-operator">{`{`}</span></div>
              <div>&nbsp;&nbsp;work: <span className="docs-token-string">&quot;Reconcile Salesforce, HubSpot, and billing records for new enterprise accounts&quot;</span>,</div>
              <div>&nbsp;&nbsp;outcome: <span className="docs-token-string">&quot;Return records updated, conflicts found, and approvals needed&quot;</span></div>
              <div><span className="docs-token-operator">{`}`}</span>);</div>
            </div>
          </div>

          <h3 className="docs-subtitle">
            <span className="docs-workflow-badge docs-workflow-badge--ticket">Job ticket</span>
            Payment with approval
          </h3>
          <p className="docs-text docs-workflow-human">
            Human parallel: Commerce ops fulfills wholesale order #7721 in checkout with manager approval before payment.
          </p>
          <div className="docs-code-block">
            <div className="docs-code-header">
              <span className="docs-lang">JAVASCRIPT</span>
            </div>
            <div className="docs-code-content">
              <div><span className="docs-token-keyword">await</span> client.workers.<span className="docs-token-function">run</span>(<span className="docs-token-operator">{`{`}</span></div>
              <div className="docs-code-line docs-ci-1">work: <span className="docs-token-string">&quot;Complete checkout for wholesale order #7721&quot;</span>,</div>
              <div className="docs-code-line docs-ci-1">resources: <span className="docs-token-operator">{`{`}</span></div>
              <div className="docs-code-line docs-ci-2">card: <span className="docs-token-string">&quot;card_vault_xxx&quot;</span>,</div>
              <div className="docs-code-line docs-ci-2">budget: <span className="docs-token-number">500</span>,</div>
              <div className="docs-code-line docs-ci-1"><span className="docs-token-operator">{`},`}</span></div>
              <div className="docs-code-line docs-ci-1">guardrails: <span className="docs-token-operator">{`{`}</span></div>
              <div className="docs-code-line docs-ci-2">approvals: [<span className="docs-token-string">&quot;payments&quot;</span>],</div>
              <div className="docs-code-line docs-ci-1"><span className="docs-token-operator">{`},`}</span></div>
              <div className="docs-code-line docs-ci-1">webhook: <span className="docs-token-string">&quot;https://you.com/grupa/events&quot;</span>,</div>
              <div><span className="docs-token-operator">{`}`}</span>);</div>
            </div>
          </div>

          <h3 className="docs-section-title docs-section-title--group">Run a business function</h3>
          <p className="docs-text">
            Delegate objectives teams already own. Use <code>mode: &quot;team&quot;</code> or persistent workers;
            GrupaAI decomposes the brief into sub-work until the outcome is met.
          </p>

          <h3 className="docs-subtitle">
            <span className="docs-workflow-badge docs-workflow-badge--function">Function brief</span>
            Marketplace worker team
          </h3>
          <p className="docs-text docs-workflow-human">
            Human parallel: Marketplace ops lead assigns the team — grow supply, demand, and matches this week.
          </p>
          <div className="docs-code-block">
            <div className="docs-code-header">
              <span className="docs-lang">JAVASCRIPT</span>
            </div>
            <div className="docs-code-content">
              <div><span className="docs-token-keyword">await</span> client.workers.<span className="docs-token-function">run</span>(<span className="docs-token-operator">{`{`}</span></div>
              <div className="docs-code-line docs-ci-1">work: <span className="docs-token-string">&quot;Grow liquidity for our AI engineer talent marketplace this week&quot;</span>,</div>
              <div className="docs-code-line docs-ci-1">worker: <span className="docs-token-operator">{`{`}</span></div>
              <div className="docs-code-line docs-ci-2">mode: <span className="docs-token-string">&quot;team&quot;</span>,</div>
              <div className="docs-code-line docs-ci-2">roles: [<span className="docs-token-string">&quot;supply&quot;</span>, <span className="docs-token-string">&quot;demand&quot;</span>, <span className="docs-token-string">&quot;ops&quot;</span>],</div>
              <div className="docs-code-line docs-ci-1"><span className="docs-token-operator">{`},`}</span></div>
              <div className="docs-code-line docs-ci-1">resources: <span className="docs-token-operator">{`{`}</span></div>
              <div className="docs-code-line docs-ci-2">credentials: <span className="docs-token-string">&quot;vault_marketplace_ops&quot;</span>,</div>
              <div className="docs-code-line docs-ci-2">budget: <span className="docs-token-number">300</span>,</div>
              <div className="docs-code-line docs-ci-1"><span className="docs-token-operator">{`},`}</span></div>
              <div className="docs-code-line docs-ci-1">outcome: <span className="docs-token-string">&quot;Return sourced candidates, qualified buyers, matches made, and next actions&quot;</span>,</div>
              <div><span className="docs-token-operator">{`}`}</span>);</div>
            </div>
          </div>

          <h3 className="docs-subtitle">
            <span className="docs-workflow-badge docs-workflow-badge--function">Function brief</span>
            Marketplace matching team
          </h3>
          <p className="docs-text docs-workflow-human">
            Human parallel: Ops assigns matchers to pair supply listings with buyer requests for the week.
          </p>
          <div className="docs-code-block">
            <div className="docs-code-header">
              <span className="docs-lang">JAVASCRIPT</span>
            </div>
            <div className="docs-code-content">
              <div><span className="docs-token-keyword">await</span> client.workers.<span className="docs-token-function">run</span>(<span className="docs-token-operator">{`{`}</span></div>
              <div className="docs-code-line docs-ci-1">work: <span className="docs-token-string">&quot;Match supply listings with buyer requests this week&quot;</span>,</div>
              <div className="docs-code-line docs-ci-1">worker: <span className="docs-token-operator">{`{`}</span></div>
              <div className="docs-code-line docs-ci-2">mode: <span className="docs-token-string">&quot;team&quot;</span>,</div>
              <div className="docs-code-line docs-ci-2">size: <span className="docs-token-number">3</span>,</div>
              <div className="docs-code-line docs-ci-1"><span className="docs-token-operator">{`}`}</span>,</div>
              <div><span className="docs-token-operator">{`}`}</span>);</div>
            </div>
          </div>

          <h3 className="docs-subtitle">
            <span className="docs-workflow-badge docs-workflow-badge--function">Function brief</span>
            Advanced: automatic worker topology
          </h3>
          <p className="docs-text docs-workflow-human">
            Human parallel: Partner assigns analyst — find and diligence pre-seed AI infra startups this month.
          </p>
          <p className="docs-text">
            Use <code>worker.mode: &quot;auto&quot;</code> when you want GrupaAI to choose
            whether the brief needs one worker or a team.
          </p>
          <div className="docs-code-block">
            <div className="docs-code-header">
              <span className="docs-lang">JAVASCRIPT</span>
            </div>
            <div className="docs-code-content">
              <div><span className="docs-token-keyword">const</span> run <span className="docs-token-operator">=</span> <span className="docs-token-keyword">await</span> client.workers.<span className="docs-token-function">run</span>(<span className="docs-token-operator">{`{`}</span></div>
              <div className="docs-code-line docs-ci-1">work: <span className="docs-token-string">&quot;Find and diligence the top AI infra startups raising pre-seed this month&quot;</span>,</div>
              <div className="docs-code-line docs-ci-1">worker: <span className="docs-token-operator">{`{`}</span> mode: <span className="docs-token-string">&quot;auto&quot;</span> <span className="docs-token-operator">{`}`}</span>,</div>
              <div className="docs-code-line docs-ci-1">resources: <span className="docs-token-operator">{`{`}</span> budget: <span className="docs-token-number">500</span> <span className="docs-token-operator">{`}`}</span>,</div>
              <div className="docs-code-line docs-ci-1">outcome: <span className="docs-token-string">&quot;Return ranked companies, diligence memo, and warm intro paths&quot;</span>,</div>
              <div><span className="docs-token-operator">{`}`}</span>);</div>
            </div>
          </div>
        </section>

        <section id="use-cases" className="docs-section">
          <h2 className="docs-section-title">Use Cases</h2>
          <p className="docs-text">
            Businesses and product categories that become easier to build with
            autonomous workers as execution infrastructure.
          </p>

          <div className="docs-grid">
            <div className="docs-card">
              <h4>Marketplaces</h4>
              <p>One worker grows supply, one worker qualifies demand, and one worker coordinates ops. Your product owns the UI; workers run the marketplace loops.</p>
              <div className="docs-code-block">
                <div className="docs-code-header">
                  <span className="docs-lang">JAVASCRIPT</span>
                </div>
                <div className="docs-code-content">
                  <div>roles: [<span className="docs-token-string">&quot;supply&quot;</span>, <span className="docs-token-string">&quot;demand&quot;</span>, <span className="docs-token-string">&quot;ops&quot;</span>]</div>
                </div>
              </div>
            </div>

            <div className="docs-card">
              <h4>Vertical SaaS that does the work</h4>
              <p>Healthcare, insurance, real estate, logistics, and recruiting products can execute portal workflows behind their own app experience.</p>
              <div className="docs-code-block">
                <div className="docs-code-header">
                  <span className="docs-lang">JAVASCRIPT</span>
                </div>
                <div className="docs-code-content">
                  <div>work: <span className="docs-token-string">&quot;Submit case through external portal...&quot;</span></div>
                </div>
              </div>
            </div>

            <div className="docs-card">
              <h4>Tech-company operations</h4>
              <p>Revenue ops, finance ops, support ops, recruiting ops, and vendor ops can run on persistent workers supervised by humans.</p>
              <div className="docs-code-block">
                <div className="docs-code-header">
                  <span className="docs-lang">JAVASCRIPT</span>
                </div>
                <div className="docs-code-content">
                  <div>worker: <span className="docs-token-operator">{`{`}</span> mode: <span className="docs-token-string">&quot;persistent&quot;</span> <span className="docs-token-operator">{`}`}</span></div>
                </div>
              </div>
            </div>

            <div className="docs-card">
              <h4>AI-native BPO</h4>
              <p>Service businesses can replace manual portal labor with worker fleets, keeping humans on exceptions, QA, and relationships.</p>
              <div className="docs-code-block">
                <div className="docs-code-header">
                  <span className="docs-lang">JAVASCRIPT</span>
                </div>
                <div className="docs-code-content">
                  <div>outcome: <span className="docs-token-string">&quot;Return queue completed and exceptions&quot;</span></div>
                </div>
              </div>
            </div>

            <div className="docs-card">
              <h4>Commerce and concierge apps</h4>
              <p>Workers can order, book, cancel, reschedule, and pay on behalf of users with explicit approval gates.</p>
              <div className="docs-code-block">
                <div className="docs-code-header">
                  <span className="docs-lang">JAVASCRIPT</span>
                </div>
                <div className="docs-code-content">
                  <div>guardrails: <span className="docs-token-operator">{`{`}</span> approvals: [<span className="docs-token-string">&quot;payments&quot;</span>] <span className="docs-token-operator">{`}`}</span></div>
                </div>
              </div>
            </div>

            <div className="docs-card">
              <h4>Autonomous GTM systems</h4>
              <p>Workers research accounts, enrich CRMs, qualify leads, run outreach, follow up, and book meetings through real tools.</p>
              <div className="docs-code-block">
                <div className="docs-code-header">
                  <span className="docs-lang">JAVASCRIPT</span>
                </div>
                <div className="docs-code-content">
                  <div>work: <span className="docs-token-string">&quot;Qualify leads and book meetings...&quot;</span></div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="billing" className="docs-section">
          <h2 className="docs-section-title">Billing</h2>
          <p className="docs-text">
            Service billing is separate from purchases a worker makes on your behalf. Review your account rate card and billing view. Charges include:
          </p>
          <ul className="docs-list">
            <li><strong>Worker active time:</strong> Execution and waits within an active run are billed. Idle identities, pending triggers, and queued turns do not reserve a desktop.</li>
            <li><strong>Availability:</strong> On-demand, part-time, full-time, and 24/7 workers can be priced by usage and volume.</li>
            <li><strong>Infrastructure included:</strong> Cloud runtime, browser sessions, model routing, traces, approvals, and worker orchestration.</li>
            <li><strong>Resources separate:</strong> External spend controlled by <code>resources.budget</code>, card limits, and approval guardrails.</li>
          </ul>
          <p className="docs-text">
            A run&apos;s <code>cost.status</code> is <code>pending</code>,{" "}
            <code>partial</code>, or <code>reported</code>. Retrieve invoice-backed
            source line items and totals from{" "}
            <code>GET /v1/runs/{"{runId}"}/cost-breakdown</code>. The response
            separates gross cost, credits, direct and shared cloud costs, external
            providers, and descendant-run costs. <code>cost.total_usd</code> is absent
            until an authoritative source reports; missing totals do not mean zero.
            The separate <code>inference_estimate</code> is excluded from billing totals.
          </p>
          <p className="docs-text">
            <code>GET /v1/billing</code> returns the authoritative tenant billing
            account. Billing mandates, setup intents, saved payment methods, and
            balance payments are available to organization administrators through the billing endpoints in the REST catalog above. Use <code>PATCH /v1/billing/auto-topup</code> to configure authorized prepaid automatic collection. Batch provider-cost summaries at <code>GET /v1/runs/costs</code> require a Grupa platform administrator or validated support session; customer admins and API keys cannot use them.
          </p>
          <p className="docs-text">
            Build autonomous businesses on GrupaAI like you build on payments or
            compute — call the API, attach resources, get outcomes back.
          </p>
          <div className="docs-callout">
            <p className="docs-callout-text">
              For enterprise rate cards and volume discounts, please <a
                href="https://calendar.app.google/CE8yHXidRV2hsSRS9"
                target="_blank"
                rel="noopener noreferrer"
                className="docs-link"
              >
                book a call
              </a> with our sales team.
            </p>
          </div>
        </section>
      </main>
    </div>
    </div>
  );
}
